/**
 * Elite Batch 4 Part 3 — Workflow Templates 313–347
 * Documents (313–320) and E-commerce (321–347)
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function docsNode(
  id: string,
  label: string,
  y: number,
  title: string,
  contentVar: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://docs.googleapis.com/v1/documents",
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "title", value: title },
          {
            key: "body",
            value: `{"content":[{"paragraph":{"elements":[{"textRun":{"content":"${contentVar}"}}]}}]}`,
          },
        ],
        variables: [
          { name: "documentId", customName: "doc_id", type: "string" },
          { name: "title", customName: "doc_title", type: "string" },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 4 PART 3 — TEMPLATES 313–347
// =============================================================================

export const ELITE_BATCH4_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 313. LEASE AGREEMENT GENERATOR (8 nodes)
  // ===================================================================

  "Lease Agreement Generator": {
    summary:
      "Automates lease agreement creation from intake to legal review. A request webhook triggers AI extraction of lease parameters, selects the right template and clauses, generates a compliant draft via AI, checks legal compliance, creates the Google Doc, logs to Sheets, emails legal for review, and alerts ops on Slack.",
    nodes: [
      trigger("Webhook: Lease agreement request", "webhook_app", {
        source: "Property Management Portal / CRM",
        event: "lease.request.created",
        howItStarts:
          "A new lease agreement request is submitted through the property management portal, CRM, or intake form",
        setup:
          "Connect your property management system or intake form to POST lease request data (tenant info, property details, lease terms, special clauses) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract lease parameters + select template/clauses
      n("2", "runCode", "Template + clause selection", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse lease request and select appropriate template + clauses
const leaseType = vars.lease_type || "residential";
const state = vars.state || "CA";
const templates = {
  residential: "standard_residential_v4",
  commercial: "commercial_nnn_v3",
  industrial: "industrial_gross_v2",
  retail: "retail_percentage_v3"
};
const requiredClauses = ["habitability", "security_deposit", "termination", "maintenance"];
if (leaseType === "commercial" || leaseType === "retail") {
  requiredClauses.push("cam_charges", "signage_rights", "exclusivity");
}
if (state === "CA" || state === "NY") {
  requiredClauses.push("rent_control_addendum");
}
const result = {
  template_id: templates[leaseType] || templates.residential,
  required_clauses: requiredClauses.join(", "),
  clause_count: requiredClauses.length,
  jurisdiction: state,
  lease_category: leaseType
};
return result;`,
          outputVariables: [
            { name: "template_id", type: "string" },
            { name: "required_clauses", type: "string" },
            { name: "clause_count", type: "number" },
            { name: "jurisdiction", type: "string" },
            { name: "lease_category", type: "string" },
          ],
        },
      }),

      // 3. AI draft generation
      aiNode(
        "3",
        "AI generate lease draft",
        520,
        "You are a real estate legal document generator. Given tenant info, property details, lease terms, template ID, and required clauses, generate a complete lease agreement draft. Include all standard sections: parties, premises, term, rent, security deposit, maintenance, insurance, default/remedies, and all required clauses. Use jurisdiction-appropriate language. Respond JSON: {lease_draft: string, parties_section: string, financial_terms: string, special_provisions: string, clause_summaries: string, word_count: number, compliance_flags: string}",
        "Template: {template_id} | Jurisdiction: {jurisdiction} | Clauses: {required_clauses} | Tenant: {tenant_name} | Property: {property_address} | Term: {lease_term} | Rent: {monthly_rent} | Deposit: {security_deposit} | Special terms: {special_terms}",
        [
          { field: "lease_draft", as: "lease_draft", type: "string" },
          { field: "parties_section", as: "parties_section", type: "string" },
          { field: "financial_terms", as: "financial_terms", type: "string" },
          {
            field: "special_provisions",
            as: "special_provisions",
            type: "string",
          },
          { field: "compliance_flags", as: "compliance_flags", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
        ],
      ),

      // 4. Legal compliance check condition
      n("4", "condition", "Legal compliance pass?", 760, {
        conditionData: [
          {
            variable: "compliance_flags",
            value: "none",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Document creation (Google Docs)
      docsNode(
        "5",
        "Create lease in Google Docs",
        1000,
        "Lease Agreement: {tenant_name} - {property_address}",
        "{lease_draft}",
      ),

      // 6. Lease record logging (Sheets)
      sheetsNode(
        "6",
        "Log lease record to Sheets",
        1240,
        "LeaseAgreements",
        '[["{tenant_name}","{property_address}","{lease_category}","{jurisdiction}","{monthly_rent}","{lease_term}","{template_id}","{clause_count}","{doc_id}","{compliance_flags}"]]',
      ),

      // 7. Legal review notification (email)
      emailNode(
        "7",
        "Email legal team for review",
        1480,
        "{legal_team_email}",
        "Lease Agreement Ready for Review: {tenant_name} - {property_address}",
        "A new lease agreement has been generated and requires legal review.\\n\\nTenant: {tenant_name}\\nProperty: {property_address}\\nType: {lease_category}\\nJurisdiction: {jurisdiction}\\nMonthly Rent: {monthly_rent}\\nTerm: {lease_term}\\nClauses Included: {clause_count}\\nCompliance Flags: {compliance_flags}\\n\\nGoogle Doc: https://docs.google.com/document/d/{doc_id}\\n\\nPlease review and approve within 48 hours.",
      ),

      // 8. Ops Slack alert
      slackNode(
        "8",
        "Slack: Notify ops team",
        1720,
        "Lease agreement generated for {tenant_name} at {property_address}\\nType: {lease_category} | Jurisdiction: {jurisdiction}\\nRent: {monthly_rent}/mo | Term: {lease_term}\\nCompliance: {compliance_flags}\\nDoc: https://docs.google.com/document/d/{doc_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // compliant -> create doc
      e("4", "7", "output_2"), // flags found -> send directly to legal
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 314. LEGAL CLAUSE LIBRARY BOT (8 nodes)
  // ===================================================================

  "Legal Clause Library Bot": {
    summary:
      "AI-powered legal clause assistant that accepts clause requests, classifies intent (search/explain/compare/draft), queries the clause library, delivers results via Slack, logs usage, and alerts legal when new clauses are needed.",
    nodes: [
      trigger("Webhook: Clause library request", "webhook_app", {
        source: "Slack Command / Legal Portal / API",
        event: "clause.request",
        howItStarts:
          "A team member requests a legal clause via Slack command, legal portal, or direct API call",
        setup:
          "Connect a Slack slash-command (/clause) or your legal portal to POST clause requests (query text, context, action type) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI classify intent
      aiNode(
        "2",
        "AI classify clause request",
        280,
        "You are a legal clause librarian. Classify this request into one of four actions: search (find existing clause), explain (explain a clause in plain language), compare (compare two clauses), or draft (create a new custom clause). Extract the clause topic, jurisdiction if mentioned, and contract type. Respond JSON: {action: string, clause_topic: string, jurisdiction: string, contract_type: string, requester_name: string, search_keywords: string, comparison_clauses: string}",
        "{webhook_payload}",
        [
          { field: "action", as: "action", type: "string" },
          { field: "clause_topic", as: "clause_topic", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "contract_type", as: "contract_type", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "search_keywords", as: "search_keywords", type: "string" },
        ],
      ),

      // 3. Clause library search (Pinecone vector search)
      n("3", "hitApi", "Search clause library (Pinecone)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "namespace", value: "legal_clauses" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "vector", value: "{search_embedding}" },
          ],
          variables: [
            {
              name: "matches[0].metadata.clause_text",
              customName: "top_clause",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "match_score",
              type: "number",
            },
            {
              name: "matches.length",
              customName: "result_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Multiple results condition
      n("4", "condition", "Multiple relevant results?", 760, {
        conditionData: [
          {
            variable: "result_count",
            value: "1",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI explanation/draft
      aiNode(
        "5",
        "AI explain, compare, or draft clause",
        1000,
        "You are a legal clause expert. Based on the action requested, do one of: (1) SEARCH: summarize the top matching clause and highlight key provisions; (2) EXPLAIN: break down the clause in plain English with risk notes; (3) COMPARE: compare the clauses side by side with pros/cons; (4) DRAFT: write a new clause for the given topic and jurisdiction. Always include a confidence rating and recommendation. Respond JSON: {response_text: string, clause_summary: string, risk_notes: string, confidence: number, recommendation: string, needs_new_clause: string}",
        "Action: {action} | Topic: {clause_topic} | Jurisdiction: {jurisdiction} | Contract type: {contract_type} | Top match: {top_clause} | Match score: {match_score} | Result count: {result_count}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "clause_summary", as: "clause_summary", type: "string" },
          { field: "risk_notes", as: "risk_notes", type: "string" },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "needs_new_clause", as: "needs_new_clause", type: "string" },
        ],
      ),

      // 6. Deliver response via Slack
      slackNode(
        "6",
        "Slack: Deliver clause response",
        1240,
        "Clause Library Result for {requester_name}\\n*Topic:* {clause_topic} | *Action:* {action}\\n\\n{response_text}\\n\\n_Risk Notes:_ {risk_notes}\\n_Confidence:_ {confidence}%",
      ),

      // 7. Usage logging (Sheets)
      sheetsNode(
        "7",
        "Log clause usage to Sheets",
        1480,
        "ClauseUsage",
        '[["{requester_name}","{action}","{clause_topic}","{jurisdiction}","{contract_type}","{match_score}","{confidence}","{needs_new_clause}"]]',
      ),

      // 8. Legal alert for new clauses
      emailNode(
        "8",
        "Email legal: New clause needed",
        1720,
        "{legal_admin_email}",
        "New Clause Request: {clause_topic} ({jurisdiction})",
        "A clause request could not be fully satisfied from the existing library.\\n\\nTopic: {clause_topic}\\nJurisdiction: {jurisdiction}\\nContract Type: {contract_type}\\nAction: {action}\\nMatch Score: {match_score}\\nRequester: {requester_name}\\n\\nPlease review and consider adding a new clause to the library.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // multiple results -> AI processes
      e("4", "5", "output_2"), // single/no results -> AI still processes (drafts)
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 315. NDA GENERATOR CHATBOT (8 nodes)
  // ===================================================================

  "NDA Generator Chatbot": {
    summary:
      "Conversational NDA generator that takes a request, asks AI clarification questions, routes by NDA type (mutual/unilateral/multilateral), pulls clauses from the library, assembles the NDA via AI, creates the Google Doc, launches a signature workflow, and logs to the NDA registry.",
    nodes: [
      trigger("Webhook: NDA generation request", "webhook_app", {
        source: "Legal Portal / Slack / CRM",
        event: "nda.request.created",
        howItStarts:
          "A team member or external partner submits an NDA request through the legal portal, Slack, or CRM integration",
        setup:
          "Connect your legal portal, Slack app, or CRM to POST NDA request data (parties, purpose, scope, duration preferences) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI clarification questions
      aiNode(
        "2",
        "AI extract NDA details + clarify",
        280,
        "You are an NDA intake specialist. Extract all NDA parameters from the request. Determine the NDA type (mutual, unilateral, multilateral), the parties, purpose, confidential information scope, duration, jurisdiction, and any special requirements. If critical info is missing, flag it. Respond JSON: {nda_type: string, disclosing_party: string, receiving_party: string, purpose: string, scope: string, duration_years: number, jurisdiction: string, special_requirements: string, missing_fields: string, party_count: number}",
        "{webhook_payload}",
        [
          { field: "nda_type", as: "nda_type", type: "string" },
          { field: "disclosing_party", as: "disclosing_party", type: "string" },
          { field: "receiving_party", as: "receiving_party", type: "string" },
          { field: "purpose", as: "purpose", type: "string" },
          { field: "scope", as: "scope", type: "string" },
          { field: "duration_years", as: "duration_years", type: "number" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
        ],
      ),

      // 3. NDA type routing condition
      n("3", "condition", "Mutual NDA required?", 520, {
        conditionData: [
          {
            variable: "nda_type",
            value: "mutual",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Clause library pull (Pinecone)
      n("4", "hitApi", "Pull NDA clauses from library", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "namespace", value: "nda_clauses" },
            { key: "topK", value: "10" },
            { key: "includeMetadata", value: "true" },
            {
              key: "filter",
              value: `{"nda_type":"${"{nda_type}"}","jurisdiction":"${"{jurisdiction}"}"}`,
            },
          ],
          variables: [
            {
              name: "matches[0].metadata.clause_text",
              customName: "confidentiality_clause",
              type: "string",
            },
            {
              name: "matches[1].metadata.clause_text",
              customName: "obligations_clause",
              type: "string",
            },
            {
              name: "matches[2].metadata.clause_text",
              customName: "remedies_clause",
              type: "string",
            },
            {
              name: "matches.length",
              customName: "clauses_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI NDA assembly
      aiNode(
        "5",
        "AI assemble complete NDA",
        1000,
        "You are an NDA drafting expert. Assemble a complete, legally sound NDA using the provided clauses, party information, and parameters. Include: recitals, definitions, confidential information scope, obligations of receiving party, exclusions, term and termination, remedies, miscellaneous provisions (governing law, severability, integration, waiver). For mutual NDAs, mirror obligations. Respond JSON: {nda_document: string, effective_date: string, signature_blocks: string, exhibit_a: string, key_terms_summary: string}",
        "Type: {nda_type} | Disclosing: {disclosing_party} | Receiving: {receiving_party} | Purpose: {purpose} | Scope: {scope} | Duration: {duration_years} years | Jurisdiction: {jurisdiction} | Special: {special_requirements} | Clauses: {confidentiality_clause} | {obligations_clause} | {remedies_clause}",
        [
          { field: "nda_document", as: "nda_document", type: "string" },
          { field: "effective_date", as: "effective_date", type: "string" },
          { field: "signature_blocks", as: "signature_blocks", type: "string" },
          {
            field: "key_terms_summary",
            as: "key_terms_summary",
            type: "string",
          },
        ],
      ),

      // 6. Document creation (Google Docs)
      docsNode(
        "6",
        "Create NDA in Google Docs",
        1240,
        "NDA: {disclosing_party} - {receiving_party} ({nda_type})",
        "{nda_document}",
      ),

      // 7. Signature workflow launch (DocuSign)
      n("7", "hitApi", "Launch DocuSign signature workflow", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://demo.docusign.net/restapi/v2.1/accounts/{docusign_account_id}/envelopes",
          headers: [
            { key: "Authorization", value: "Bearer {docusign_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "emailSubject",
              value:
                "NDA for Signature: {disclosing_party} & {receiving_party}",
            },
            { key: "status", value: "sent" },
            {
              key: "documents",
              value:
                '[{"documentBase64":"{nda_document_base64}","name":"NDA_{disclosing_party}_{receiving_party}.pdf","documentId":"1"}]',
            },
            {
              key: "recipients",
              value:
                '{"signers":[{"email":"{disclosing_email}","name":"{disclosing_party}","recipientId":"1","routingOrder":"1"},{"email":"{receiving_email}","name":"{receiving_party}","recipientId":"2","routingOrder":"2"}]}',
            },
          ],
          variables: [
            { name: "envelopeId", customName: "envelope_id", type: "string" },
            { name: "status", customName: "envelope_status", type: "string" },
          ],
        },
      }),

      // 8. NDA registry logging (Sheets)
      sheetsNode(
        "8",
        "Log to NDA registry",
        1720,
        "NDARegistry",
        '[["{disclosing_party}","{receiving_party}","{nda_type}","{jurisdiction}","{duration_years}","{effective_date}","{doc_id}","{envelope_id}","{purpose}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // mutual -> clause library (mutual clauses)
      e("3", "4", "output_2"), // unilateral -> clause library (unilateral clauses)
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 316. POWER OF ATTORNEY FORM BUILDER (8 nodes)
  // ===================================================================

  "Power of Attorney Form Builder": {
    summary:
      "Builds customized Power of Attorney documents based on type and jurisdiction. Routes by POA type, generates an AI draft, verifies jurisdiction-specific requirements, creates the Google Doc with witness/notary sections, notifies the attorney for review, and logs to a document tracker.",
    nodes: [
      trigger("Webhook: POA form request", "webhook_app", {
        source: "Legal Portal / Client Intake Form",
        event: "poa.request.created",
        howItStarts:
          "A client or attorney submits a Power of Attorney request through the legal portal or client intake form",
        setup:
          "Connect your legal intake system to POST POA requests (principal info, agent info, POA type, powers granted, jurisdiction) to the workflow URL.",
        inputFields: [],
      }),

      // 2. POA type routing condition
      aiNode(
        "2",
        "AI extract POA parameters",
        280,
        "Extract all Power of Attorney parameters from this request. Determine POA type (general, limited/special, durable, healthcare/medical, financial, springing). Identify principal, agent(s), successor agent(s), powers granted, limitations, effective date, jurisdiction, and whether notarization is required. Respond JSON: {poa_type: string, principal_name: string, principal_address: string, agent_name: string, agent_address: string, successor_agent: string, powers_granted: string, limitations: string, effective_date: string, jurisdiction: string, is_durable: string, notary_required: string}",
        "{webhook_payload}",
        [
          { field: "poa_type", as: "poa_type", type: "string" },
          { field: "principal_name", as: "principal_name", type: "string" },
          { field: "agent_name", as: "agent_name", type: "string" },
          { field: "powers_granted", as: "powers_granted", type: "string" },
          { field: "limitations", as: "limitations", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "is_durable", as: "is_durable", type: "string" },
          { field: "notary_required", as: "notary_required", type: "string" },
        ],
      ),

      // 3. POA type routing
      n("3", "condition", "Healthcare POA (special requirements)?", 520, {
        conditionData: [
          {
            variable: "poa_type",
            value: "healthcare",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI draft generation
      aiNode(
        "4",
        "AI generate POA document",
        760,
        "You are a legal document specialist for Powers of Attorney. Draft a complete POA document for the specified type and jurisdiction. Include: caption, recitals, grant of authority, specific powers, limitations, durability clause (if applicable), revocation provisions, successor agent provisions, governing law, execution block. For healthcare POAs include HIPAA authorization and living will integration. Respond JSON: {poa_document: string, execution_requirements: string, witness_count: number, notary_block: string, hipaa_authorization: string, statutory_form_reference: string}",
        "Type: {poa_type} | Principal: {principal_name} | Agent: {agent_name} | Powers: {powers_granted} | Limitations: {limitations} | Jurisdiction: {jurisdiction} | Durable: {is_durable}",
        [
          { field: "poa_document", as: "poa_document", type: "string" },
          {
            field: "execution_requirements",
            as: "execution_requirements",
            type: "string",
          },
          { field: "witness_count", as: "witness_count", type: "number" },
          { field: "notary_block", as: "notary_block", type: "string" },
          {
            field: "statutory_form_reference",
            as: "statutory_form_reference",
            type: "string",
          },
        ],
      ),

      // 5. Jurisdiction requirement verification (runCode)
      n("5", "runCode", "Verify jurisdiction requirements", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify jurisdiction-specific POA requirements
const state = vars.jurisdiction;
const poaType = vars.poa_type;
const requirements = {
  witnesses_required: 2,
  notary_required: true,
  statutory_form: false,
  special_notes: []
};
// State-specific rules
const statesRequiringStatutoryForm = ["CA", "NY", "FL", "TX", "IL"];
if (statesRequiringStatutoryForm.includes(state)) {
  requirements.statutory_form = true;
  requirements.special_notes.push("Must use state statutory form or substantially conform");
}
if (state === "FL") {
  requirements.witnesses_required = 2;
  requirements.special_notes.push("FL requires two subscribing witnesses");
}
if (poaType === "healthcare" && ["CA", "NY"].includes(state)) {
  requirements.special_notes.push("Healthcare POA must include specific statutory language");
}
const result = {
  jurisdiction_valid: true,
  witnesses_needed: requirements.witnesses_required,
  notary_needed: requirements.notary_required ? "yes" : "no",
  uses_statutory_form: requirements.statutory_form ? "yes" : "no",
  jurisdiction_notes: requirements.special_notes.join("; ") || "Standard requirements apply"
};
return result;`,
          outputVariables: [
            { name: "jurisdiction_valid", type: "boolean" },
            { name: "witnesses_needed", type: "number" },
            { name: "notary_needed", type: "string" },
            { name: "uses_statutory_form", type: "string" },
            { name: "jurisdiction_notes", type: "string" },
          ],
        },
      }),

      // 6. Document creation with witness/notary sections (Google Docs)
      docsNode(
        "6",
        "Create POA in Google Docs",
        1240,
        "Power of Attorney ({poa_type}): {principal_name} to {agent_name}",
        "{poa_document}\\n\\n--- WITNESS ACKNOWLEDGMENT ---\\n{notary_block}\\n\\nJurisdiction Notes: {jurisdiction_notes}",
      ),

      // 7. Attorney review notification (email)
      emailNode(
        "7",
        "Email attorney for review",
        1480,
        "{attorney_email}",
        "POA Document Ready for Review: {principal_name} ({poa_type})",
        "A Power of Attorney document has been generated and requires your review.\\n\\nType: {poa_type}\\nPrincipal: {principal_name}\\nAgent: {agent_name}\\nJurisdiction: {jurisdiction}\\nWitnesses Required: {witnesses_needed}\\nNotary Required: {notary_needed}\\nStatutory Form: {uses_statutory_form}\\nJurisdiction Notes: {jurisdiction_notes}\\n\\nDocument: https://docs.google.com/document/d/{doc_id}",
      ),

      // 8. Document tracker logging (Sheets)
      sheetsNode(
        "8",
        "Log to document tracker",
        1720,
        "POATracker",
        '[["{principal_name}","{agent_name}","{poa_type}","{jurisdiction}","{is_durable}","{witnesses_needed}","{notary_needed}","{doc_id}","{uses_statutory_form}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // healthcare -> AI with HIPAA additions
      e("3", "4", "output_2"), // other types -> standard AI draft
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 317. SOP CREATOR BOT (8 nodes)
  // ===================================================================

  "SOP Creator Bot": {
    summary:
      "Creates Standard Operating Procedures from intake requests. AI drafts an outline, a requester approval gate ensures alignment, AI expands into a full SOP, code formats it to template standards, the document is created in Google Docs, the department is notified for review, and the SOP is registered in the index.",
    nodes: [
      trigger("Webhook: SOP creation request", "webhook_app", {
        source: "Ops Portal / Slack / Jira",
        event: "sop.request.created",
        howItStarts:
          "A team lead or process owner submits an SOP creation request through the ops portal, Slack command, or Jira ticket",
        setup:
          "Connect your ops portal, Slack slash-command, or Jira webhook to POST SOP requests (process name, department, scope, key steps, compliance requirements) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI draft outline
      aiNode(
        "2",
        "AI draft SOP outline",
        280,
        "You are an SOP documentation specialist. Given a process description, create a structured SOP outline with: purpose, scope, responsibilities, definitions, prerequisites, step-by-step procedure sections, quality checks, exception handling, revision history template. Number all sections. Respond JSON: {sop_title: string, purpose: string, scope: string, section_count: number, outline_text: string, department: string, process_owner: string, requester_email: string, estimated_pages: number, compliance_standards: string}",
        "{webhook_payload}",
        [
          { field: "sop_title", as: "sop_title", type: "string" },
          { field: "purpose", as: "purpose", type: "string" },
          { field: "section_count", as: "section_count", type: "number" },
          { field: "outline_text", as: "outline_text", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "process_owner", as: "process_owner", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "compliance_standards",
            as: "compliance_standards",
            type: "string",
          },
        ],
      ),

      // 3. Requester approval gate
      n("3", "approvalGate", "Requester approves SOP outline", 520, {
        approvalGateData: {
          message:
            "SOP Outline for Review: {sop_title}\\n\\nPurpose: {purpose}\\nDepartment: {department}\\nSections: {section_count}\\nCompliance: {compliance_standards}\\n\\nOutline:\\n{outline_text}\\n\\nApprove to proceed with full SOP expansion.",
        },
      }),

      // 4. AI full SOP expansion
      aiNode(
        "4",
        "AI expand full SOP content",
        760,
        "You are an SOP writing expert. Expand the approved outline into a complete, detailed Standard Operating Procedure. Each section must have: clear step numbering, responsible party, expected inputs/outputs, time estimates, quality checkpoints, and troubleshooting tips. Use imperative voice. Include diagrams placeholder markers where helpful. Respond JSON: {full_sop: string, word_count: number, procedure_steps: number, quality_checkpoints: number, exception_scenarios: number, training_notes: string}",
        "Title: {sop_title} | Department: {department} | Compliance: {compliance_standards} | Approved outline: {outline_text}",
        [
          { field: "full_sop", as: "full_sop", type: "string" },
          { field: "word_count", as: "word_count", type: "number" },
          { field: "procedure_steps", as: "procedure_steps", type: "number" },
          {
            field: "quality_checkpoints",
            as: "quality_checkpoints",
            type: "number",
          },
          { field: "training_notes", as: "training_notes", type: "string" },
        ],
      ),

      // 5. Template formatting (runCode)
      n("5", "runCode", "Format SOP to template standard", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format SOP with standard template structure
const now = new Date().toISOString().split("T")[0];
const sopNumber = "SOP-" + vars.department.substring(0,3).toUpperCase() + "-" + Date.now().toString().slice(-6);
const header = [
  "STANDARD OPERATING PROCEDURE",
  "Document #: " + sopNumber,
  "Department: " + vars.department,
  "Title: " + vars.sop_title,
  "Effective Date: " + now,
  "Revision: 1.0",
  "Process Owner: " + vars.process_owner,
  "Compliance: " + vars.compliance_standards,
  "---"
].join("\\n");
const result = {
  formatted_sop: header + "\\n\\n" + vars.full_sop,
  sop_number: sopNumber,
  effective_date: now,
  revision: "1.0"
};
return result;`,
          outputVariables: [
            { name: "formatted_sop", type: "string" },
            { name: "sop_number", type: "string" },
            { name: "effective_date", type: "string" },
            { name: "revision", type: "string" },
          ],
        },
      }),

      // 6. SOP document creation (Google Docs)
      docsNode(
        "6",
        "Create SOP in Google Docs",
        1240,
        "{sop_number}: {sop_title}",
        "{formatted_sop}",
      ),

      // 7. Department review notification
      emailNode(
        "7",
        "Email department for review",
        1480,
        "{requester_email}",
        "SOP Ready for Review: {sop_number} - {sop_title}",
        "Your SOP has been created and is ready for departmental review.\\n\\nSOP Number: {sop_number}\\nTitle: {sop_title}\\nDepartment: {department}\\nRevision: {revision}\\nProcedure Steps: {procedure_steps}\\nQuality Checkpoints: {quality_checkpoints}\\nWord Count: {word_count}\\n\\nDocument: https://docs.google.com/document/d/{doc_id}\\n\\nPlease review with your team and submit any revisions within 5 business days.",
      ),

      // 8. SOP index registration (Sheets)
      sheetsNode(
        "8",
        "Register in SOP index",
        1720,
        "SOPIndex",
        '[["{sop_number}","{sop_title}","{department}","{process_owner}","{revision}","{effective_date}","{procedure_steps}","{quality_checkpoints}","{compliance_standards}","{doc_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 318. SETTLEMENT AGREEMENT GENERATOR (8 nodes)
  // ===================================================================

  "Settlement Agreement Generator": {
    summary:
      "Generates settlement agreements by routing through the appropriate template, pulling clauses from the library, drafting with AI-calculated payment schedules, running consistency checks, creating the document in Google Docs, distributing to counsel via email, and logging to the settlement tracker.",
    nodes: [
      trigger("Webhook: Settlement agreement request", "webhook_app", {
        source: "Legal Case Management / Mediation Portal",
        event: "settlement.request.created",
        howItStarts:
          "A mediator, attorney, or case manager initiates a settlement agreement request from the case management system",
        setup:
          "Connect your legal case management system or mediation portal to POST settlement parameters (case info, parties, terms, amounts, payment structure) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Template routing condition
      aiNode(
        "2",
        "AI extract settlement parameters",
        280,
        "Extract all settlement agreement parameters from this request. Identify: case type (employment, personal injury, commercial, insurance, IP), parties (plaintiff/defendant), settlement amount, payment structure (lump sum, installment, structured), confidentiality requirements, release scope (general, limited), jurisdiction, and special conditions. Respond JSON: {case_type: string, plaintiff_name: string, defendant_name: string, settlement_amount: number, payment_structure: string, installment_count: number, confidentiality_level: string, release_scope: string, jurisdiction: string, special_conditions: string, case_number: string, plaintiff_counsel_email: string, defendant_counsel_email: string}",
        "{webhook_payload}",
        [
          { field: "case_type", as: "case_type", type: "string" },
          { field: "plaintiff_name", as: "plaintiff_name", type: "string" },
          { field: "defendant_name", as: "defendant_name", type: "string" },
          {
            field: "settlement_amount",
            as: "settlement_amount",
            type: "number",
          },
          {
            field: "payment_structure",
            as: "payment_structure",
            type: "string",
          },
          {
            field: "confidentiality_level",
            as: "confidentiality_level",
            type: "string",
          },
          { field: "release_scope", as: "release_scope", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "case_number", as: "case_number", type: "string" },
          {
            field: "plaintiff_counsel_email",
            as: "plaintiff_counsel_email",
            type: "string",
          },
          {
            field: "defendant_counsel_email",
            as: "defendant_counsel_email",
            type: "string",
          },
        ],
      ),

      // 3. Template routing by case type
      n("3", "condition", "Employment settlement (special terms)?", 520, {
        conditionData: [
          {
            variable: "case_type",
            value: "employment",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Clause library pull + AI draft with payment schedule
      aiNode(
        "4",
        "AI draft settlement + payment schedule",
        760,
        "You are a settlement agreement drafting specialist. Draft a complete settlement agreement including: recitals, definitions, settlement payment terms (with installment schedule if applicable), general/limited release of claims, confidentiality provisions, non-disparagement, indemnification, representations and warranties, tax provisions, enforcement, governing law, and execution blocks. For employment cases include: ADEA waiver (21-day consideration + 7-day revocation), EEOC/DFEH references, return of company property. Calculate installment amounts if applicable. Respond JSON: {settlement_document: string, payment_schedule: string, total_with_interest: number, release_language: string, special_waivers: string, execution_deadline: string, consistency_score: number}",
        "Case: {case_number} | Type: {case_type} | Plaintiff: {plaintiff_name} | Defendant: {defendant_name} | Amount: ${settlement_amount} | Payment: {payment_structure} | Confidentiality: {confidentiality_level} | Release: {release_scope} | Jurisdiction: {jurisdiction} | Special: {special_conditions}",
        [
          {
            field: "settlement_document",
            as: "settlement_document",
            type: "string",
          },
          { field: "payment_schedule", as: "payment_schedule", type: "string" },
          {
            field: "total_with_interest",
            as: "total_with_interest",
            type: "number",
          },
          { field: "release_language", as: "release_language", type: "string" },
          { field: "special_waivers", as: "special_waivers", type: "string" },
          {
            field: "consistency_score",
            as: "consistency_score",
            type: "number",
          },
        ],
      ),

      // 5. Consistency check (runCode)
      n("5", "runCode", "Cross-reference consistency check", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify settlement agreement internal consistency
const issues = [];
const amount = parseFloat(vars.settlement_amount) || 0;
const totalWithInterest = parseFloat(vars.total_with_interest) || 0;
// Check payment schedule math
if (totalWithInterest > 0 && Math.abs(totalWithInterest - amount) > amount * 0.15) {
  issues.push("Payment total deviates >15% from settlement amount");
}
// Check required sections present
const doc = vars.settlement_document || "";
const requiredSections = ["RELEASE", "CONFIDENTIAL", "GOVERNING LAW"];
requiredSections.forEach(section => {
  if (!doc.toUpperCase().includes(section)) {
    issues.push("Missing section: " + section);
  }
});
// Employment-specific checks
if (vars.case_type === "employment" && !doc.toUpperCase().includes("ADEA")) {
  issues.push("Employment settlement missing ADEA waiver");
}
const result = {
  consistency_passed: issues.length === 0 ? "true" : "false",
  issue_count: issues.length,
  issues_found: issues.join("; ") || "No issues found",
  review_priority: issues.length > 2 ? "high" : issues.length > 0 ? "medium" : "standard"
};
return result;`,
          outputVariables: [
            { name: "consistency_passed", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "issues_found", type: "string" },
            { name: "review_priority", type: "string" },
          ],
        },
      }),

      // 6. Document creation (Google Docs)
      docsNode(
        "6",
        "Create settlement in Google Docs",
        1240,
        "Settlement Agreement: {plaintiff_name} v. {defendant_name} ({case_number})",
        "{settlement_document}",
      ),

      // 7. Counsel distribution (email)
      emailNode(
        "7",
        "Email settlement to counsel",
        1480,
        "{plaintiff_counsel_email}",
        "Settlement Agreement Draft: {plaintiff_name} v. {defendant_name} ({case_number})",
        "Please find the draft settlement agreement for your review.\\n\\nCase: {case_number}\\nParties: {plaintiff_name} v. {defendant_name}\\nType: {case_type}\\nSettlement Amount: ${settlement_amount}\\nPayment Structure: {payment_structure}\\nPayment Schedule: {payment_schedule}\\nConsistency Check: {issues_found}\\nReview Priority: {review_priority}\\n\\nDocument: https://docs.google.com/document/d/{doc_id}\\n\\nPlease review and return comments within 5 business days.",
      ),

      // 8. Settlement logging (Sheets)
      sheetsNode(
        "8",
        "Log to settlement tracker",
        1720,
        "SettlementTracker",
        '[["{case_number}","{case_type}","{plaintiff_name}","{defendant_name}","{settlement_amount}","{payment_structure}","{confidentiality_level}","{release_scope}","{consistency_passed}","{doc_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // employment -> special template
      e("3", "4", "output_2"), // other -> standard template
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 319. WILL AND ESTATE PLANNING BOT (9 nodes)
  // ===================================================================

  "Will and Estate Planning Bot": {
    summary:
      "Full estate planning workflow: intake questionnaire, AI needs assessment routing (simple will vs trust vs complex estate), AI drafting, state execution requirement verification, document package assembly, client/attorney delivery, inventory tracking, review reminder scheduling, and secure registry archival.",
    nodes: [
      trigger("Webhook: Estate planning intake", "webhook_app", {
        source: "Estate Planning Portal / Attorney CRM",
        event: "estate.intake.completed",
        howItStarts:
          "A client completes the estate planning questionnaire through the portal or an attorney submits the intake on their behalf",
        setup:
          "Connect your estate planning portal or attorney CRM to POST completed intake data (client info, assets, beneficiaries, wishes, existing documents) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Needs assessment routing condition
      aiNode(
        "2",
        "AI assess estate planning needs",
        280,
        "You are an estate planning intake analyst. Analyze this client questionnaire to determine the appropriate planning level: simple_will (single, minimal assets, straightforward beneficiaries), trust_needed (married, minor children, real estate, business interests, or tax planning needed), complex_estate (high net worth, multi-state property, charitable giving, special needs beneficiaries, business succession). Extract all relevant details. Respond JSON: {planning_level: string, client_name: string, client_email: string, marital_status: string, has_minor_children: string, estimated_estate_value: number, has_real_estate: string, has_business: string, state: string, beneficiary_count: number, special_needs_beneficiary: string, existing_documents: string, attorney_email: string, key_concerns: string}",
        "{webhook_payload}",
        [
          { field: "planning_level", as: "planning_level", type: "string" },
          { field: "client_name", as: "client_name", type: "string" },
          { field: "client_email", as: "client_email", type: "string" },
          { field: "state", as: "state", type: "string" },
          {
            field: "estimated_estate_value",
            as: "estimated_estate_value",
            type: "number",
          },
          {
            field: "has_minor_children",
            as: "has_minor_children",
            type: "string",
          },
          {
            field: "beneficiary_count",
            as: "beneficiary_count",
            type: "number",
          },
          { field: "attorney_email", as: "attorney_email", type: "string" },
          { field: "key_concerns", as: "key_concerns", type: "string" },
          { field: "planning_level", as: "planning_level", type: "string" },
        ],
      ),

      // 3. Routing condition
      n("3", "condition", "Trust planning needed?", 520, {
        conditionData: [
          {
            variable: "planning_level",
            value: "simple_will",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI will/trust drafting
      aiNode(
        "4",
        "AI draft estate documents",
        760,
        "You are an estate planning document drafter. Based on the planning level and client details, draft the appropriate documents. For simple_will: Last Will and Testament with executor appointment, guardianship (if minors), and asset distribution. For trust_needed: Revocable Living Trust + Pour-Over Will + Certificate of Trust. For complex_estate: all of the above plus Asset Protection Trust provisions, charitable remainder trust, special needs trust. Include state-specific language. Respond JSON: {primary_document: string, supporting_documents: string, document_package_list: string, executor_provisions: string, guardian_provisions: string, trust_provisions: string, tax_planning_notes: string, document_count: number}",
        "Level: {planning_level} | Client: {client_name} | State: {state} | Estate: ${estimated_estate_value} | Minor children: {has_minor_children} | Beneficiaries: {beneficiary_count} | Concerns: {key_concerns}",
        [
          { field: "primary_document", as: "primary_document", type: "string" },
          {
            field: "supporting_documents",
            as: "supporting_documents",
            type: "string",
          },
          {
            field: "document_package_list",
            as: "document_package_list",
            type: "string",
          },
          { field: "trust_provisions", as: "trust_provisions", type: "string" },
          {
            field: "tax_planning_notes",
            as: "tax_planning_notes",
            type: "string",
          },
          { field: "document_count", as: "document_count", type: "number" },
        ],
      ),

      // 5. State execution requirement verification (runCode)
      n("5", "runCode", "Verify state execution requirements", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify state-specific will/trust execution requirements
const state = vars.state;
const level = vars.planning_level;
const req = {
  witness_count: 2,
  notary_required: false,
  self_proving_affidavit: true,
  holographic_valid: false,
  special_notes: []
};
// State-specific witness requirements
if (state === "VT") { req.witness_count = 3; }
if (["LA"].includes(state)) {
  req.notary_required = true;
  req.special_notes.push("Louisiana requires notarial testament form");
}
if (["TX", "AZ", "AR", "NC", "VA"].includes(state)) {
  req.holographic_valid = true;
}
req.self_proving_affidavit = true; // Available in all states except DC and MD with limitations
if (level !== "simple_will") {
  req.notary_required = true;
  req.special_notes.push("Trust requires notarization for real estate transfers");
}
const result = {
  witnesses_required: req.witness_count,
  notary_required: req.notary_required ? "yes" : "no",
  self_proving: req.self_proving_affidavit ? "yes" : "no",
  execution_instructions: "Sign in the presence of " + req.witness_count + " witnesses" + (req.notary_required ? " and a notary public" : "") + ". " + req.special_notes.join(". "),
  state_notes: req.special_notes.join("; ") || "Standard execution requirements"
};
return result;`,
          outputVariables: [
            { name: "witnesses_required", type: "number" },
            { name: "notary_required", type: "string" },
            { name: "self_proving", type: "string" },
            { name: "execution_instructions", type: "string" },
            { name: "state_notes", type: "string" },
          ],
        },
      }),

      // 6. Document package assembly (Google Docs)
      docsNode(
        "6",
        "Assemble document package in Google Docs",
        1240,
        "Estate Plan: {client_name} ({planning_level})",
        "{primary_document}\\n\\n=== SUPPORTING DOCUMENTS ===\\n{supporting_documents}\\n\\n=== EXECUTION INSTRUCTIONS ===\\n{execution_instructions}",
      ),

      // 7. Client/attorney delivery (email)
      emailNode(
        "7",
        "Email to client and attorney",
        1480,
        "{attorney_email}",
        "Estate Planning Documents Ready: {client_name}",
        "The estate planning document package for {client_name} is ready for review.\\n\\nPlanning Level: {planning_level}\\nState: {state}\\nEstimated Estate Value: ${estimated_estate_value}\\nDocuments Included: {document_package_list}\\nDocument Count: {document_count}\\n\\nExecution Requirements:\\n{execution_instructions}\\n\\nTax Planning Notes: {tax_planning_notes}\\nState Notes: {state_notes}\\n\\nDocument: https://docs.google.com/document/d/{doc_id}\\n\\nPlease schedule a review meeting with the client.",
      ),

      // 8. Document inventory tracking (Sheets)
      sheetsNode(
        "8",
        "Log to document inventory",
        1720,
        "EstateInventory",
        '[["{client_name}","{planning_level}","{state}","{estimated_estate_value}","{document_count}","{document_package_list}","{witnesses_required}","{notary_required}","{doc_id}"]]',
      ),

      // 9. Review reminder scheduling (delay) + secure registry
      n("9", "delayAction", "Schedule annual review reminder", 1960, {
        delayAction: { time: 365, timeFormat: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // trust/complex -> trust-level drafting
      e("3", "4", "output_2"), // simple will -> basic drafting
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 320. WITNESS STATEMENT COLLECTOR BOT (8 nodes)
  // ===================================================================

  "Witness Statement Collector Bot": {
    summary:
      "Manages witness statement collection for legal cases. Sets up the case, generates secure intake links, notifies witnesses via email, processes submitted statements via webhook, formats with AI, checks completeness, creates a case workspace Google Doc, and notifies the legal team on Slack.",
    nodes: [
      trigger("Webhook: Case setup for witness collection", "webhook_app", {
        source: "Case Management System / Legal Portal",
        event: "case.witness_collection.initiated",
        howItStarts:
          "An attorney or case manager initiates witness statement collection for a case through the case management system",
        setup:
          "Connect your case management system to POST case details (case number, witness list, questions, deadline) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Secure intake link generation (runCode)
      n("2", "runCode", "Generate secure intake links", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique, secure intake links for each witness
const crypto = require("crypto");
const witnesses = (vars.witness_names || "").split(",").map(w => w.trim()).filter(Boolean);
const links = witnesses.map((name, i) => {
  const token = crypto.randomBytes(16).toString("hex");
  return {
    name: name,
    email: (vars.witness_emails || "").split(",")[i]?.trim() || "",
    link: "https://intake.yourfirm.com/statement/" + vars.case_number + "/" + token,
    token: token
  };
});
const result = {
  intake_links: JSON.stringify(links),
  witness_count: witnesses.length,
  first_witness_name: links[0]?.name || "",
  first_witness_email: links[0]?.email || "",
  first_witness_link: links[0]?.link || "",
  case_number: vars.case_number,
  deadline: vars.deadline || "14 days from today"
};
return result;`,
          outputVariables: [
            { name: "intake_links", type: "string" },
            { name: "witness_count", type: "number" },
            { name: "first_witness_name", type: "string" },
            { name: "first_witness_email", type: "string" },
            { name: "first_witness_link", type: "string" },
            { name: "case_number", type: "string" },
            { name: "deadline", type: "string" },
          ],
        },
      }),

      // 3. Witness notification (email)
      emailNode(
        "3",
        "Email witnesses with intake links",
        520,
        "{first_witness_email}",
        "Witness Statement Request: Case {case_number}",
        "Dear {first_witness_name},\\n\\nYou have been identified as a witness in Case {case_number}. We request your written statement regarding the events in question.\\n\\nPlease use this secure link to submit your statement:\\n{first_witness_link}\\n\\nDeadline: {deadline}\\n\\nYour statement will be kept confidential and used only for legal proceedings. If you have questions, please contact the legal team.\\n\\nThank you for your cooperation.",
      ),

      // 4. Submission webhook processing
      aiNode(
        "4",
        "AI process submitted statement",
        760,
        "You are a legal statement processor. Read this witness statement submission and extract: the witness name, date of statement, narrative summary, key facts mentioned, people referenced, dates/times referenced, locations mentioned, and any inconsistencies or gaps. Assess completeness on a 0-100 scale. Respond JSON: {witness_name: string, statement_date: string, narrative_summary: string, key_facts: string, people_referenced: string, dates_times: string, locations: string, completeness_score: number, gaps_identified: string, statement_length: number}",
        "{webhook_payload}",
        [
          {
            field: "witness_name",
            as: "statement_witness_name",
            type: "string",
          },
          { field: "statement_date", as: "statement_date", type: "string" },
          {
            field: "narrative_summary",
            as: "narrative_summary",
            type: "string",
          },
          { field: "key_facts", as: "key_facts", type: "string" },
          {
            field: "completeness_score",
            as: "completeness_score",
            type: "number",
          },
          { field: "gaps_identified", as: "gaps_identified", type: "string" },
          { field: "statement_length", as: "statement_length", type: "number" },
        ],
      ),

      // 5. AI statement formatting
      aiNode(
        "5",
        "AI format statement for case file",
        1000,
        "You are a legal document formatter. Take this witness statement and format it into a proper legal witness statement format with: header (case number, witness name, date), oath/affirmation language, numbered paragraphs, signature block, and notary acknowledgment section. Preserve all factual content exactly. Respond JSON: {formatted_statement: string, paragraph_count: number, format_quality: string}",
        "Case: {case_number} | Witness: {statement_witness_name} | Date: {statement_date} | Summary: {narrative_summary} | Key facts: {key_facts}",
        [
          {
            field: "formatted_statement",
            as: "formatted_statement",
            type: "string",
          },
          { field: "paragraph_count", as: "paragraph_count", type: "number" },
          { field: "format_quality", as: "format_quality", type: "string" },
        ],
      ),

      // 6. Completeness check condition
      n("6", "condition", "Statement complete (score >= 70)?", 1240, {
        conditionData: [
          {
            variable: "completeness_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Case workspace document creation (Google Docs)
      docsNode(
        "7",
        "Add to case workspace (Google Docs)",
        1480,
        "Witness Statement: {statement_witness_name} - Case {case_number}",
        "{formatted_statement}",
      ),

      // 8. Legal team notification (Slack)
      slackNode(
        "8",
        "Slack: Notify legal team",
        1720,
        "Witness statement received for Case {case_number}\\nWitness: {statement_witness_name}\\nCompleteness: {completeness_score}/100\\nParagraphs: {paragraph_count}\\nGaps: {gaps_identified}\\nDoc: https://docs.google.com/document/d/{doc_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // complete -> add to case workspace
      e("6", "3", "output_2"), // incomplete -> re-notify witness for additional info
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 321. AI PRODUCT FEED OPTIMIZATION (8 nodes)
  // ===================================================================

  "AI Product Feed Optimization": {
    summary:
      "Optimizes product catalog feeds for marketplaces. Pulls catalog and channel requirements, analyzes performance and disapproval data, generates AI-optimized titles and descriptions, routes major rewrites through merchandising approval, updates the product system via Shopify API, logs before/after comparisons, and tracks feed health.",
    nodes: [
      trigger("Schedule: Daily product feed optimization", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Runs daily to scan product catalog for feed optimization opportunities across Google Shopping, Facebook, Amazon, and other channels",
        setup:
          "Activate this workflow and connect your Shopify store, Google Merchant Center, and feed management tool. The workflow will automatically identify underperforming listings.",
        inputFields: [],
      }),

      // 2. Catalog + channel requirement pull
      shopifyNode(
        "2",
        "Pull product catalog from Shopify",
        280,
        "GET",
        "products.json?limit=50&fields=id,title,body_html,product_type,tags,variants,images",
        [],
        [
          { name: "products[0].id", customName: "product_id", type: "string" },
          {
            name: "products[0].title",
            customName: "current_title",
            type: "string",
          },
          {
            name: "products[0].body_html",
            customName: "current_description",
            type: "string",
          },
          {
            name: "products[0].product_type",
            customName: "product_type",
            type: "string",
          },
          {
            name: "products[0].tags",
            customName: "product_tags",
            type: "string",
          },
        ],
      ),

      // 3. Performance + disapproval data pull (Google Merchant Center)
      n("3", "hitApi", "Pull Merchant Center performance data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products/{product_id}",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          body: [],
          variables: [
            {
              name: "destinations[0].status",
              customName: "feed_status",
              type: "string",
            },
            {
              name: "issues",
              customName: "disapproval_issues",
              type: "string",
            },
            { name: "clicks", customName: "current_clicks", type: "number" },
            {
              name: "impressions",
              customName: "current_impressions",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI title/description optimization
      aiNode(
        "4",
        "AI optimize product listing",
        760,
        "You are a product feed optimization expert for Google Shopping and marketplace channels. Analyze the current title and description, performance data, and any disapproval issues. Generate optimized versions following best practices: front-load key attributes in title (brand + product + key specs), include search terms, fix disapproval issues, keep under character limits (150 title, 5000 description). Rate the change magnitude. Respond JSON: {optimized_title: string, optimized_description: string, seo_keywords: string, change_magnitude: string, disapproval_fixes: string, expected_improvement: string, title_length: number, description_length: number}",
        "Current title: {current_title} | Current description: {current_description} | Type: {product_type} | Tags: {product_tags} | Feed status: {feed_status} | Disapprovals: {disapproval_issues} | Clicks: {current_clicks} | Impressions: {current_impressions}",
        [
          { field: "optimized_title", as: "optimized_title", type: "string" },
          {
            field: "optimized_description",
            as: "optimized_description",
            type: "string",
          },
          { field: "change_magnitude", as: "change_magnitude", type: "string" },
          {
            field: "disapproval_fixes",
            as: "disapproval_fixes",
            type: "string",
          },
          {
            field: "expected_improvement",
            as: "expected_improvement",
            type: "string",
          },
        ],
      ),

      // 5. Major rewrite condition
      n("5", "condition", "Major rewrite (needs approval)?", 1000, {
        conditionData: [
          {
            variable: "change_magnitude",
            value: "major",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Merchandising approval gate
      n("6", "approvalGate", "Merchandising approves major rewrite", 1240, {
        approvalGateData: {
          message:
            "Major product listing rewrite requires approval:\\n\\nProduct: {product_id} ({product_type})\\n\\nCurrent Title: {current_title}\\nOptimized Title: {optimized_title}\\n\\nChange Magnitude: {change_magnitude}\\nDisapproval Fixes: {disapproval_fixes}\\nExpected Improvement: {expected_improvement}",
        },
      }),

      // 7. Product system update (Shopify)
      shopifyNode(
        "7",
        "Update product in Shopify",
        1480,
        "PUT",
        "products/{product_id}.json",
        [
          {
            key: "product",
            value:
              '{"title":"{optimized_title}","body_html":"{optimized_description}"}',
          },
        ],
        [
          {
            name: "product.id",
            customName: "updated_product_id",
            type: "string",
          },
          {
            name: "product.updated_at",
            customName: "update_timestamp",
            type: "string",
          },
        ],
      ),

      // 8. Before/after logging + feed health tracking (Sheets)
      sheetsNode(
        "8",
        "Log optimization + feed health",
        1720,
        "FeedOptimization",
        '[["{product_id}","{current_title}","{optimized_title}","{change_magnitude}","{disapproval_fixes}","{expected_improvement}","{feed_status}","{current_clicks}","{current_impressions}","{update_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // major rewrite -> approval gate
      e("5", "7", "output_2"), // minor tweak -> auto-update
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 322. ACCESSORY UPSELL AFTER DEVICE PURCHASE (7 nodes)
  // ===================================================================

  "Accessory Upsell After Device Purchase": {
    summary:
      "Sends personalized accessory recommendations after a device purchase. Detects the device purchase via webhook, pulls compatible accessories from Shopify, generates AI-personalized recommendations, delays for optimal timing, sends the upsell email, tracks purchases, and logs bundle performance.",
    nodes: [
      trigger("Webhook: Device purchase completed", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "order.created",
        howItStarts:
          "A customer completes a device purchase (phone, laptop, tablet, camera, etc.) in your store",
        setup:
          "Connect Shopify webhook on orders/create. The workflow filters for device-category products and triggers accessory upsell sequences.",
        inputFields: [],
      }),

      // 2. Compatible accessories pull (Shopify API)
      aiNode(
        "2",
        "AI extract order + identify device type",
        280,
        "Read this order webhook and extract: customer email, first name, device product name, device category (phone, laptop, tablet, camera, gaming, audio), device brand, device model, order ID, order total. Respond JSON: {customer_email: string, first_name: string, device_name: string, device_category: string, device_brand: string, device_model: string, order_id: string, order_total: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "device_name", as: "device_name", type: "string" },
          { field: "device_category", as: "device_category", type: "string" },
          { field: "device_brand", as: "device_brand", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
        ],
      ),

      // 3. Pull compatible accessories from Shopify
      shopifyNode(
        "3",
        "Pull compatible accessories",
        520,
        "GET",
        "products.json?product_type=accessories&tag={device_category},{device_brand}&limit=10",
        [],
        [
          {
            name: "products[0].title",
            customName: "accessory_1_name",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "accessory_1_price",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "accessory_2_name",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "accessory_2_price",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "accessory_3_name",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "accessory_3_price",
            type: "string",
          },
        ],
      ),

      // 4. AI personalized recommendations
      aiNode(
        "4",
        "AI generate personalized upsell copy",
        760,
        "You are an e-commerce upsell specialist. Write a personalized accessory recommendation email for someone who just bought a device. Highlight how each accessory enhances their device experience. Use the customer first name, reference their specific device, and create urgency with a limited-time bundle discount. Keep it friendly and helpful, not pushy. Respond JSON: {email_subject: string, email_body: string, hero_recommendation: string, bundle_discount_percent: number, bundle_total: string}",
        "Customer: {first_name} | Device: {device_name} ({device_brand} {device_category}) | Accessory 1: {accessory_1_name} (${accessory_1_price}) | Accessory 2: {accessory_2_name} (${accessory_2_price}) | Accessory 3: {accessory_3_name} (${accessory_3_price})",
        [
          { field: "email_subject", as: "upsell_subject", type: "string" },
          { field: "email_body", as: "upsell_body", type: "string" },
          {
            field: "hero_recommendation",
            as: "hero_recommendation",
            type: "string",
          },
          {
            field: "bundle_discount_percent",
            as: "bundle_discount",
            type: "number",
          },
        ],
      ),

      // 5. Delivery timing delay
      n("5", "delayAction", "Wait 2 days for device delivery", 1000, {
        delayAction: { time: 2, timeFormat: "days" },
      }),

      // 6. Upsell email send
      emailNode(
        "6",
        "Send accessory upsell email",
        1240,
        "{customer_email}",
        "{upsell_subject}",
        "{upsell_body}",
      ),

      // 7. Purchase tracking + bundle performance logging (Sheets)
      sheetsNode(
        "7",
        "Log bundle performance",
        1480,
        "AccessoryUpsells",
        '[["{order_id}","{customer_email}","{device_name}","{device_category}","{device_brand}","{hero_recommendation}","{bundle_discount}","{accessory_1_name}","{accessory_2_name}","{accessory_3_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 323. ACCOUNT VERIFICATION COMPLETION THANK-YOU (6 nodes)
  // ===================================================================

  "Account Verification Completion Thank-You": {
    summary:
      "Sends a personalized thank-you message after account verification. Pulls the customer profile, generates AI copy with next-step guidance, routes by customer tier, sends the thank-you email, and logs funnel progression.",
    nodes: [
      trigger("Webhook: Account verification complete", "webhook_app", {
        source: "Identity Verification Service / Auth System",
        event: "verification.completed",
        howItStarts:
          "A customer completes their account verification (KYC, email, phone, or identity document verification)",
        setup:
          "Connect your identity verification service (Stripe Identity, Jumio, etc.) or auth system to POST verification completion events to the workflow URL.",
        inputFields: [],
      }),

      // 2. Customer profile pull + extraction
      aiNode(
        "2",
        "AI extract verification details",
        280,
        "Extract verification completion details from this webhook. Find: customer email, name, verification type (email, phone, identity, KYC), customer tier (free, premium, enterprise), account age in days, verification score, and any features unlocked by verification. Respond JSON: {customer_email: string, customer_name: string, verification_type: string, customer_tier: string, account_age_days: number, features_unlocked: string, verification_score: number}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "verification_type",
            as: "verification_type",
            type: "string",
          },
          { field: "customer_tier", as: "customer_tier", type: "string" },
          { field: "account_age_days", as: "account_age_days", type: "number" },
          {
            field: "features_unlocked",
            as: "features_unlocked",
            type: "string",
          },
        ],
      ),

      // 3. AI thank-you + next-step generation
      aiNode(
        "3",
        "AI generate thank-you + next steps",
        520,
        "Write a warm, personalized thank-you email for completing account verification. Reference the specific verification type, highlight newly unlocked features, and provide clear next steps based on their tier. For premium/enterprise tiers, include exclusive perks. Keep it concise and action-oriented. Respond JSON: {email_subject: string, email_body: string, primary_cta: string, secondary_cta: string}",
        "Customer: {customer_name} | Tier: {customer_tier} | Verification: {verification_type} | Features unlocked: {features_unlocked} | Account age: {account_age_days} days",
        [
          { field: "email_subject", as: "thankyou_subject", type: "string" },
          { field: "email_body", as: "thankyou_body", type: "string" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
        ],
      ),

      // 4. Tier routing condition
      n("4", "condition", "Premium or Enterprise tier?", 760, {
        conditionData: [
          {
            variable: "customer_tier",
            value: "free",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Thank-you email send
      emailNode(
        "5",
        "Send verification thank-you email",
        1000,
        "{customer_email}",
        "{thankyou_subject}",
        "{thankyou_body}",
      ),

      // 6. Funnel logging (Sheets)
      sheetsNode(
        "6",
        "Log verification funnel",
        1240,
        "VerificationFunnel",
        '[["{customer_email}","{customer_name}","{verification_type}","{customer_tier}","{account_age_days}","{features_unlocked}","{primary_cta}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // premium/enterprise -> personalized email
      e("4", "5", "output_2"), // free -> standard email
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 324. ACCOUNT-CREATION PROMPT AFTER GUEST CHECKOUT (7 nodes)
  // ===================================================================

  "Account-Creation Prompt After Guest Checkout": {
    summary:
      "Converts guest checkout customers to registered accounts. Detects guest orders, checks for existing accounts, suppresses repeat-guest messages, generates AI benefits messaging, sends a delayed account creation email, tracks conversions, and logs results.",
    nodes: [
      trigger("Webhook: Guest checkout completed", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "order.created",
        howItStarts:
          "A customer completes a purchase as a guest (no account) on your e-commerce store",
        setup:
          "Connect Shopify orders/create webhook. The workflow detects guest checkouts (no customer account ID) and initiates the account creation prompt sequence.",
        inputFields: [],
      }),

      // 2. Existing account check + extraction
      aiNode(
        "2",
        "AI extract guest order details",
        280,
        "Read this order webhook and determine if the buyer checked out as guest (no account). Extract: email, first name, order total, items purchased, has_account (true/false based on customer.id presence). Respond JSON: {guest_email: string, first_name: string, order_total: number, items_purchased: string, order_id: string, has_account: string, order_count: number}",
        "{webhook_payload}",
        [
          { field: "guest_email", as: "guest_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "items_purchased", as: "items_purchased", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "has_account", as: "has_account", type: "string" },
          { field: "order_count", as: "order_count", type: "number" },
        ],
      ),

      // 3. Existing account check condition
      n("3", "condition", "Guest checkout (no account)?", 520, {
        conditionData: [
          {
            variable: "has_account",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Repeat-guest suppression (runCode)
      n("4", "runCode", "Suppress repeat guest prompts", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Suppress account creation prompt for guests who have been prompted before recently
const orderCount = parseInt(vars.order_count) || 1;
const suppressThreshold = 3; // Don't prompt after 3rd email
const result = {
  should_send: orderCount <= suppressThreshold ? "yes" : "no",
  prompt_number: Math.min(orderCount, suppressThreshold),
  incentive_tier: orderCount === 1 ? "none" : orderCount === 2 ? "small" : "medium",
  suppression_reason: orderCount > suppressThreshold ? "Max prompts reached" : "none"
};
return result;`,
          outputVariables: [
            { name: "should_send", type: "string" },
            { name: "prompt_number", type: "number" },
            { name: "incentive_tier", type: "string" },
            { name: "suppression_reason", type: "string" },
          ],
        },
      }),

      // 5. AI benefits messaging
      aiNode(
        "5",
        "AI generate account creation benefits",
        1000,
        "Write a compelling account creation email for a guest checkout customer. Highlight benefits: order tracking, faster checkout, wish lists, exclusive member deals, early access to sales. If incentive_tier is small, offer 5% off next order; if medium, offer 10% off. Reference their recent purchase. Keep it short and action-oriented. Respond JSON: {email_subject: string, email_body: string, incentive_code: string, incentive_value: string}",
        "Customer: {first_name} | Email: {guest_email} | Order total: ${order_total} | Items: {items_purchased} | Incentive tier: {incentive_tier} | Prompt number: {prompt_number}",
        [
          { field: "email_subject", as: "account_subject", type: "string" },
          { field: "email_body", as: "account_body", type: "string" },
          { field: "incentive_code", as: "incentive_code", type: "string" },
          { field: "incentive_value", as: "incentive_value", type: "string" },
        ],
      ),

      // 6. Delayed account creation email
      emailNode(
        "6",
        "Send account creation prompt email",
        1240,
        "{guest_email}",
        "{account_subject}",
        "{account_body}",
      ),

      // 7. Conversion tracking + logging (Sheets)
      sheetsNode(
        "7",
        "Log guest-to-account conversion",
        1480,
        "GuestConversions",
        '[["{order_id}","{guest_email}","{first_name}","{order_total}","{items_purchased}","{incentive_tier}","{incentive_value}","{prompt_number}","{should_send}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // guest -> check suppression
      e("3", "7", "output_2"), // has account -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 325. AMAZON FBA INVENTORY SYNC (8 nodes)
  // ===================================================================

  "Amazon FBA Inventory Sync": {
    summary:
      "Keeps Shopify and Amazon FBA inventory synchronized. Runs on a 2-hour schedule, pulls Shopify inventory, queries Amazon FBA quantities, calculates discrepancies via code, checks thresholds, submits inventory updates, verifies feed processing, and logs sync results to Sheets.",
    nodes: [
      trigger("Schedule: Amazon FBA inventory sync", "schedule", {
        schedule: "Every 2 hours",
        howItStarts:
          "Runs every 2 hours to synchronize inventory between your Shopify store and Amazon FBA warehouses",
        setup:
          "Activate this workflow and connect your Shopify store and Amazon Seller credentials. Ensure your SKU/ASIN mapping is maintained in the configured Google Sheet.",
        inputFields: [],
      }),

      // 2. Shopify inventory pull
      shopifyNode(
        "2",
        "Pull Shopify inventory levels",
        280,
        "GET",
        "inventory_levels.json?location_ids={shopify_location_id}&limit=50",
        [],
        [
          {
            name: "inventory_levels[0].inventory_item_id",
            customName: "shopify_item_id",
            type: "string",
          },
          {
            name: "inventory_levels[0].available",
            customName: "shopify_quantity",
            type: "number",
          },
          {
            name: "inventory_levels.length",
            customName: "shopify_sku_count",
            type: "number",
          },
        ],
      ),

      // 3. FBA quantity pull (Amazon SP-API)
      n("3", "hitApi", "Pull Amazon FBA inventory (SP-API)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sellingpartnerapi-na.amazon.com/fba/inventory/v1/summaries?details=true&granularityType=Marketplace&granularityId={marketplace_id}&marketplaceIds={marketplace_id}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "payload.inventorySummaries[0].asin",
              customName: "amazon_asin",
              type: "string",
            },
            {
              name: "payload.inventorySummaries[0].sellerSku",
              customName: "amazon_sku",
              type: "string",
            },
            {
              name: "payload.inventorySummaries[0].inventoryDetails.fulfillableQuantity",
              customName: "fba_quantity",
              type: "number",
            },
            {
              name: "payload.inventorySummaries.length",
              customName: "amazon_sku_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Discrepancy calculation (runCode)
      n("4", "runCode", "Calculate inventory discrepancies", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate discrepancy between Shopify and Amazon FBA inventory
const shopifyQty = parseInt(vars.shopify_quantity) || 0;
const fbaQty = parseInt(vars.fba_quantity) || 0;
const discrepancy = Math.abs(shopifyQty - fbaQty);
const discrepancyPercent = shopifyQty > 0 ? Math.round((discrepancy / shopifyQty) * 100) : 100;
const direction = shopifyQty > fbaQty ? "shopify_higher" : shopifyQty < fbaQty ? "fba_higher" : "in_sync";
const needsUpdate = discrepancy > 0;
const threshold = 5; // 5% discrepancy threshold
const result = {
  discrepancy_count: discrepancy,
  discrepancy_percent: discrepancyPercent,
  exceeds_threshold: discrepancyPercent > threshold ? "true" : "false",
  sync_direction: direction,
  recommended_quantity: Math.min(shopifyQty, fbaQty),
  needs_update: needsUpdate ? "true" : "false",
  shopify_total: shopifyQty,
  fba_total: fbaQty
};
return result;`,
          outputVariables: [
            { name: "discrepancy_count", type: "number" },
            { name: "discrepancy_percent", type: "number" },
            { name: "exceeds_threshold", type: "string" },
            { name: "sync_direction", type: "string" },
            { name: "recommended_quantity", type: "number" },
            { name: "needs_update", type: "string" },
          ],
        },
      }),

      // 5. Discrepancy condition
      n("5", "condition", "Discrepancy exceeds threshold?", 1000, {
        conditionData: [
          {
            variable: "exceeds_threshold",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Inventory update submission (Amazon SP-API)
      n("6", "hitApi", "Submit Amazon inventory update feed", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sellingpartnerapi-na.amazon.com/feeds/2021-06-30/feeds",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "feedType", value: "POST_INVENTORY_AVAILABILITY_DATA" },
            { key: "marketplaceIds", value: '["{marketplace_id}"]' },
            { key: "inputFeedDocumentId", value: "{feed_document_id}" },
          ],
          variables: [
            { name: "feedId", customName: "feed_id", type: "string" },
            {
              name: "processingStatus",
              customName: "feed_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Feed verification
      n("7", "hitApi", "Verify feed processing result", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://sellingpartnerapi-na.amazon.com/feeds/2021-06-30/feeds/{feed_id}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "processingStatus",
              customName: "final_feed_status",
              type: "string",
            },
            {
              name: "resultFeedDocumentId",
              customName: "result_doc_id",
              type: "string",
            },
          ],
        },
      }),

      // 8. Sync logging (Sheets)
      sheetsNode(
        "8",
        "Log sync results to Sheets",
        1720,
        "FBASync",
        '[["{amazon_sku}","{amazon_asin}","{shopify_quantity}","{fba_quantity}","{discrepancy_count}","{discrepancy_percent}","{sync_direction}","{feed_id}","{final_feed_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // exceeds threshold -> submit update
      e("5", "8", "output_2"), // within threshold -> just log
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 326. AMAZON INVENTORY QUANTITY UPDATE (7 nodes)
  // ===================================================================

  "Amazon Inventory Quantity Update": {
    summary:
      "Processes bulk or event-driven Amazon inventory quantity updates. Validates SKU/ASIN mappings, builds feed payloads, submits to Amazon, polls for processing results, classifies errors, and notifies ops on Slack.",
    nodes: [
      trigger("Webhook: Inventory quantity update batch", "webhook_app", {
        source: "ERP / Warehouse Management / Internal API",
        event: "inventory.batch.update",
        howItStarts:
          "An ERP system, warehouse management system, or internal tool sends a batch of inventory quantity updates for Amazon listings",
        setup:
          "Connect your ERP or WMS to POST inventory update batches (array of SKU + quantity pairs) to the workflow URL.",
        inputFields: [],
      }),

      // 2. SKU/ASIN validation (runCode)
      n("2", "runCode", "Validate SKU/ASIN mappings", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and validate inventory update batch
const payload = vars.webhook_payload || "{}";
let items = [];
try {
  const parsed = JSON.parse(payload);
  items = parsed.items || parsed.inventory || [];
} catch(e) { items = []; }
const validated = [];
const invalid = [];
items.forEach(item => {
  if (item.sku && (item.quantity !== undefined) && item.quantity >= 0) {
    validated.push(item);
  } else {
    invalid.push({ sku: item.sku || "missing", reason: !item.sku ? "missing_sku" : "invalid_quantity" });
  }
});
const result = {
  valid_count: validated.length,
  invalid_count: invalid.length,
  total_submitted: items.length,
  validated_items: JSON.stringify(validated),
  invalid_items: JSON.stringify(invalid),
  batch_id: "BATCH-" + Date.now()
};
return result;`,
          outputVariables: [
            { name: "valid_count", type: "number" },
            { name: "invalid_count", type: "number" },
            { name: "total_submitted", type: "number" },
            { name: "validated_items", type: "string" },
            { name: "invalid_items", type: "string" },
            { name: "batch_id", type: "string" },
          ],
        },
      }),

      // 3. Feed payload build
      aiNode(
        "3",
        "AI build Amazon feed payload",
        520,
        "Convert this validated inventory update batch into an Amazon Inventory Loader flat file format. Each item needs: sku, quantity, fulfillment-channel (DEFAULT for MFN, AMAZON_NA for FBA), and leadtime-to-ship. Respond JSON: {feed_content: string, item_count: number, feed_format: string, estimated_processing_minutes: number}",
        "Validated items: {validated_items} | Batch ID: {batch_id}",
        [
          { field: "feed_content", as: "feed_content", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "feed_format", as: "feed_format", type: "string" },
        ],
      ),

      // 4. Amazon feed submission
      n("4", "hitApi", "Submit inventory feed to Amazon", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sellingpartnerapi-na.amazon.com/feeds/2021-06-30/feeds",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "feedType", value: "POST_INVENTORY_AVAILABILITY_DATA" },
            { key: "marketplaceIds", value: '["{marketplace_id}"]' },
          ],
          variables: [
            { name: "feedId", customName: "feed_id", type: "string" },
            {
              name: "processingStatus",
              customName: "feed_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Processing result poll
      n("5", "hitApi", "Poll feed processing result", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://sellingpartnerapi-na.amazon.com/feeds/2021-06-30/feeds/{feed_id}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "processingStatus",
              customName: "processing_result",
              type: "string",
            },
            {
              name: "resultFeedDocumentId",
              customName: "result_document_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Error classification condition
      n("6", "condition", "Feed processing successful?", 1240, {
        conditionData: [
          {
            variable: "processing_result",
            value: "DONE",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Ops notification (Slack)
      slackNode(
        "7",
        "Slack: Notify ops of feed result",
        1480,
        "Amazon Inventory Update: {batch_id}\\nItems submitted: {total_submitted} | Valid: {valid_count} | Invalid: {invalid_count}\\nFeed ID: {feed_id}\\nStatus: {processing_result}\\nInvalid items: {invalid_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // success -> notify with summary
      e("6", "7", "output_2"), // error -> notify with error details
    ],
  },

  // ===================================================================
  // 327. AMAZON LISTING SYNC (9 nodes)
  // ===================================================================

  "Amazon Listing Sync": {
    summary:
      "Synchronizes product catalog with Amazon listings. Pulls the local catalog, queries Amazon listing state, normalizes attributes via code, routes new vs update paths, submits listing feeds, monitors processing, logs to a health dashboard, handles errors with retry/escalation, and maintains a SKU audit trail.",
    nodes: [
      trigger("Schedule: Amazon listing sync", "schedule", {
        schedule: "Every 6 hours",
        howItStarts:
          "Runs every 6 hours to synchronize your product catalog with Amazon product listings, ensuring all attributes, pricing, and availability are current",
        setup:
          "Activate this workflow and connect your Shopify store and Amazon Seller credentials. Configure your product-to-ASIN mapping in the connected Google Sheet.",
        inputFields: [],
      }),

      // 2. Product catalog pull
      shopifyNode(
        "2",
        "Pull product catalog",
        280,
        "GET",
        "products.json?limit=50&fields=id,title,body_html,vendor,product_type,variants,tags,images",
        [],
        [
          { name: "products[0].id", customName: "product_id", type: "string" },
          {
            name: "products[0].title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "products[0].body_html",
            customName: "product_description",
            type: "string",
          },
          { name: "products[0].vendor", customName: "brand", type: "string" },
          {
            name: "products[0].variants[0].price",
            customName: "price",
            type: "string",
          },
          {
            name: "products[0].variants[0].sku",
            customName: "sku",
            type: "string",
          },
        ],
      ),

      // 3. Amazon listing state pull
      n("3", "hitApi", "Pull Amazon listing status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sellingpartnerapi-na.amazon.com/listings/2021-08-01/items/{seller_id}/{sku}?marketplaceIds={marketplace_id}&includedData=summaries,attributes,issues",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
          ],
          body: [],
          variables: [
            { name: "sku", customName: "amazon_sku", type: "string" },
            { name: "summaries[0].asin", customName: "asin", type: "string" },
            {
              name: "summaries[0].status",
              customName: "listing_status",
              type: "string",
            },
            { name: "issues", customName: "listing_issues", type: "string" },
          ],
        },
      }),

      // 4. Attribute normalization (runCode)
      n("4", "runCode", "Normalize attributes for Amazon", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize Shopify product attributes to Amazon format
const title = (vars.product_title || "").substring(0, 200); // Amazon title limit
const description = (vars.product_description || "").replace(/<[^>]*>/g, "").substring(0, 2000);
const price = parseFloat(vars.price) || 0;
const isNewListing = !vars.asin || vars.listing_status === "NOT_FOUND";
const result = {
  normalized_title: title,
  normalized_description: description,
  normalized_price: price.toFixed(2),
  is_new_listing: isNewListing ? "true" : "false",
  has_issues: (vars.listing_issues && vars.listing_issues !== "[]") ? "true" : "false",
  sync_action: isNewListing ? "CREATE" : "UPDATE"
};
return result;`,
          outputVariables: [
            { name: "normalized_title", type: "string" },
            { name: "normalized_description", type: "string" },
            { name: "normalized_price", type: "string" },
            { name: "is_new_listing", type: "string" },
            { name: "has_issues", type: "string" },
            { name: "sync_action", type: "string" },
          ],
        },
      }),

      // 5. New vs update routing condition
      n("5", "condition", "New listing (needs creation)?", 1000, {
        conditionData: [
          {
            variable: "is_new_listing",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Listing feed submission
      n("6", "hitApi", "Submit Amazon listing feed", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://sellingpartnerapi-na.amazon.com/listings/2021-08-01/items/{seller_id}/{sku}?marketplaceIds={marketplace_id}",
          headers: [
            { key: "x-amz-access-token", value: "{amazon_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "productType", value: "{product_type}" },
            {
              key: "attributes",
              value:
                '{"item_name":[{"value":"{normalized_title}"}],"product_description":[{"value":"{normalized_description}"}],"purchasable_offer":[{"our_price":[{"schedule":[{"value_with_tax":"{normalized_price}"}]}]}]}',
            },
          ],
          variables: [
            { name: "status", customName: "submission_status", type: "string" },
            {
              name: "submissionId",
              customName: "submission_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Listing health dashboard (Sheets)
      sheetsNode(
        "7",
        "Log to listing health dashboard",
        1480,
        "ListingHealth",
        '[["{sku}","{asin}","{product_title}","{sync_action}","{listing_status}","{has_issues}","{submission_status}","{normalized_price}","{listing_issues}"]]',
      ),

      // 8. Error retry/escalation
      slackNode(
        "8",
        "Slack: Listing sync alert",
        1720,
        "Amazon Listing Sync: {sync_action}\\nSKU: {sku} | ASIN: {asin}\\nProduct: {product_title}\\nStatus: {submission_status}\\nIssues: {listing_issues}",
      ),

      // 9. SKU audit trail
      sheetsNode(
        "9",
        "Log SKU audit trail",
        1960,
        "SKUAuditTrail",
        '[["{sku}","{asin}","{sync_action}","{submission_status}","{normalized_title}","{normalized_price}","{listing_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // new listing -> create
      e("5", "6", "output_2"), // existing -> update
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 328. ANNIVERSARY OF FIRST PURCHASE (7 nodes)
  // ===================================================================

  "Anniversary of First Purchase": {
    summary:
      "Celebrates customer purchase anniversaries with personalized offers. Scans daily for anniversaries, hydrates customer value from HubSpot, generates AI-personalized messages and offers, creates unique discount codes, sends anniversary emails, tracks redemptions, and logs performance.",
    nodes: [
      trigger("Schedule: Daily anniversary scan", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to identify customers whose first purchase anniversary falls on today, triggering a personalized celebration campaign",
        setup:
          "Activate this workflow and connect your Shopify store, HubSpot CRM, and email service. The workflow scans order history for purchase anniversaries.",
        inputFields: [],
      }),

      // 2. Customer value hydration (HubSpot)
      n("2", "hitApi", "Pull customer data from HubSpot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"first_purchase_date","operator":"EQ","value":"{today_date}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","total_revenue","hs_object_id","lifecyclestage","num_associated_deals"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "total_revenue",
              type: "number",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.num_associated_deals",
              customName: "purchase_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI message + offer generation
      aiNode(
        "3",
        "AI generate anniversary message + offer",
        520,
        "Write a heartfelt, personalized purchase anniversary email. Reference their loyalty duration, total spending, and purchase history. Scale the offer by customer value: low (<$200 LTV) = 10% off, medium ($200-$1000) = 15% off + free shipping, high (>$1000) = 20% off + gift. Include product recommendations based on their purchase patterns. Respond JSON: {email_subject: string, email_body: string, offer_type: string, discount_percent: number, bonus_perk: string, recommended_products: string, value_tier: string}",
        "Customer: {first_name} | Email: {customer_email} | Total Revenue: ${total_revenue} | Purchases: {purchase_count} | Lifecycle: {lifecycle_stage}",
        [
          { field: "email_subject", as: "anniversary_subject", type: "string" },
          { field: "email_body", as: "anniversary_body", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "bonus_perk", as: "bonus_perk", type: "string" },
          { field: "value_tier", as: "value_tier", type: "string" },
        ],
      ),

      // 4. Unique discount code (runCode)
      n("4", "runCode", "Generate unique anniversary discount code", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique anniversary discount code
const prefix = "ANNIV";
const year = new Date().getFullYear();
const random = Math.random().toString(36).substring(2, 8).toUpperCase();
const discount = parseInt(vars.discount_percent) || 10;
const code = prefix + year + "-" + random;
const result = {
  discount_code: code,
  discount_value: discount + "%",
  expiry_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  code_type: "percentage"
};
return result;`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "discount_value", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "code_type", type: "string" },
          ],
        },
      }),

      // 5. Anniversary email send
      emailNode(
        "5",
        "Send anniversary celebration email",
        1000,
        "{customer_email}",
        "{anniversary_subject}",
        "{anniversary_body}\\n\\nYour exclusive code: {discount_code} ({discount_value} off)\\nExpires: {expiry_date}\\n\\nBonus: {bonus_perk}",
      ),

      // 6. Redemption tracking (condition placeholder for webhook return)
      sheetsNode(
        "6",
        "Log anniversary campaign",
        1240,
        "AnniversaryCampaigns",
        '[["{customer_email}","{first_name}","{total_revenue}","{value_tier}","{discount_code}","{discount_value}","{bonus_perk}","{purchase_count}"]]',
      ),

      // 7. Performance logging (Sheets)
      slackNode(
        "7",
        "Slack: Anniversary campaign summary",
        1480,
        "Anniversary email sent to {first_name} ({customer_email})\\nValue tier: {value_tier} | LTV: ${total_revenue}\\nOffer: {discount_value} off + {bonus_perk}\\nCode: {discount_code} (expires {expiry_date})",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 329. ATTENTIVE ABANDONED CART SMS (8 nodes)
  // ===================================================================

  "Attentive Abandoned Cart SMS": {
    summary:
      "Recovers abandoned carts via SMS through Attentive. Processes cart abandonment webhooks, pulls cart contents, verifies SMS consent, generates AI SMS copy, routes by value tier for incentives, sends via Attentive, tracks recovery, and logs performance.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A customer abandons their cart after adding items but not completing checkout within the configured timeframe (default 1 hour)",
        setup:
          "Connect your Shopify abandoned checkout webhook or cart tracking system to POST abandoned cart data to the workflow URL.",
        inputFields: [],
      }),

      // 2. Cart contents + identity pull
      aiNode(
        "2",
        "AI extract cart and customer data",
        280,
        "Read this abandoned cart webhook and extract: customer phone, email, first name, cart items (names and prices), cart total, cart item count, time since last activity, customer SMS opt-in status. Respond JSON: {customer_phone: string, customer_email: string, first_name: string, cart_items: string, cart_total: number, item_count: number, sms_opted_in: string, cart_id: string, checkout_url: string}",
        "{webhook_payload}",
        [
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "cart_items", as: "cart_items", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "sms_opted_in", as: "sms_opted_in", type: "string" },
          { field: "cart_id", as: "cart_id", type: "string" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
        ],
      ),

      // 3. SMS consent check condition
      n("3", "condition", "SMS consent verified?", 520, {
        conditionData: [
          {
            variable: "sms_opted_in",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI SMS copy generation
      aiNode(
        "4",
        "AI generate cart recovery SMS",
        760,
        "Write a concise abandoned cart SMS (max 160 chars). Reference the highest-value item by name, include a sense of urgency (items may sell out), and include the checkout link placeholder. Be friendly, not pushy. If cart total > $100, mention free shipping. Respond JSON: {sms_text: string, char_count: number, urgency_level: string, incentive_type: string}",
        "Customer: {first_name} | Items: {cart_items} | Total: ${cart_total} | Item count: {item_count} | Checkout: {checkout_url}",
        [
          { field: "sms_text", as: "sms_text", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "incentive_type", as: "incentive_type", type: "string" },
        ],
      ),

      // 5. Value-tier incentive routing
      n("5", "condition", "High-value cart (> $150)?", 1000, {
        conditionData: [
          {
            variable: "cart_total",
            value: "150",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Attentive SMS send
      n("6", "hitApi", "Send SMS via Attentive", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/me/messages",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "to", value: "{customer_phone}" },
            { key: "body", value: "{sms_text}" },
            { key: "messagingServiceSid", value: "{attentive_service_id}" },
          ],
          variables: [
            { name: "id", customName: "message_id", type: "string" },
            { name: "status", customName: "send_status", type: "string" },
          ],
        },
      }),

      // 7. Recovery tracking
      sheetsNode(
        "7",
        "Log recovery attempt",
        1480,
        "CartRecoverySMS",
        '[["{cart_id}","{customer_phone}","{first_name}","{cart_total}","{item_count}","{cart_items}","{urgency_level}","{incentive_type}","{message_id}","{send_status}"]]',
      ),

      // 8. Performance logging (Sheets)
      slackNode(
        "8",
        "Slack: Cart recovery summary",
        1720,
        "Abandoned cart SMS sent\\nCustomer: {first_name} ({customer_phone})\\nCart: ${cart_total} ({item_count} items)\\nIncentive: {incentive_type}\\nMessage ID: {message_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // consented -> generate SMS
      e("3", "7", "output_2"), // no consent -> log only
      e("4", "5"),
      e("5", "6", "output_1"), // high value -> send with incentive
      e("5", "6", "output_2"), // standard -> send without extra incentive
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 330. ATTENTIVE BACK-IN-STOCK SMS ALERT (7 nodes)
  // ===================================================================

  "Attentive Back-in-Stock SMS Alert": {
    summary:
      "Notifies waitlisted customers via SMS when out-of-stock products are restocked. Processes restock webhooks, pulls waitlist subscribers, validates consent, generates urgency-calibrated AI SMS, sends via Attentive, tracks conversions, and logs product performance.",
    nodes: [
      trigger("Webhook: Product restocked", "webhook_app", {
        source: "Shopify / Inventory Management",
        event: "inventory.restock",
        howItStarts:
          "A previously out-of-stock product is restocked and inventory levels cross the availability threshold",
        setup:
          "Connect your Shopify inventory webhook or inventory management system to POST restock events (product ID, variant ID, quantity added) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Waitlist subscriber pull
      aiNode(
        "2",
        "AI extract restock details",
        280,
        "Read this restock webhook and extract: product ID, product name, variant info, quantity restocked, current stock level, product price, product URL, number of waitlist subscribers, and top waitlist subscriber phone numbers. Respond JSON: {product_id: string, product_name: string, variant_name: string, quantity_restocked: number, current_stock: number, product_price: number, product_url: string, waitlist_count: number, subscriber_phone: string, subscriber_name: string}",
        "{webhook_payload}",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "variant_name", as: "variant_name", type: "string" },
          {
            field: "quantity_restocked",
            as: "quantity_restocked",
            type: "number",
          },
          { field: "current_stock", as: "current_stock", type: "number" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "waitlist_count", as: "waitlist_count", type: "number" },
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
        ],
      ),

      // 3. Consent validation condition
      n("3", "condition", "SMS consent valid?", 520, {
        conditionData: [
          {
            variable: "subscriber_phone",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI urgency-calibrated SMS
      aiNode(
        "4",
        "AI generate urgency-calibrated SMS",
        760,
        "Write a back-in-stock SMS notification (max 160 chars). Calibrate urgency based on stock vs waitlist ratio: if stock < waitlist, high urgency (items will sell out fast); if stock > waitlist, moderate urgency. Include product name, price, and buy link. Respond JSON: {sms_text: string, urgency_level: string, scarcity_note: string}",
        "Product: {product_name} ({variant_name}) | Price: ${product_price} | Stock: {current_stock} | Waitlist: {waitlist_count} | URL: {product_url}",
        [
          { field: "sms_text", as: "bis_sms_text", type: "string" },
          { field: "urgency_level", as: "bis_urgency", type: "string" },
        ],
      ),

      // 5. Attentive send
      n("5", "hitApi", "Send back-in-stock SMS via Attentive", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.attentivemobile.com/v1/me/messages",
          headers: [
            { key: "Authorization", value: "Bearer {attentive_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "to", value: "{subscriber_phone}" },
            { key: "body", value: "{bis_sms_text}" },
          ],
          variables: [
            { name: "id", customName: "bis_message_id", type: "string" },
            { name: "status", customName: "bis_send_status", type: "string" },
          ],
        },
      }),

      // 6. Conversion tracking
      sheetsNode(
        "6",
        "Log back-in-stock notifications",
        1240,
        "BackInStockSMS",
        '[["{product_id}","{product_name}","{variant_name}","{product_price}","{quantity_restocked}","{current_stock}","{waitlist_count}","{bis_urgency}","{bis_message_id}","{bis_send_status}"]]',
      ),

      // 7. Product performance logging
      slackNode(
        "7",
        "Slack: Back-in-stock alert summary",
        1480,
        "Back-in-stock SMS sent for {product_name} ({variant_name})\\nStock: {current_stock} | Waitlist: {waitlist_count}\\nUrgency: {bis_urgency}\\nPrice: ${product_price}\\nSMS sent: {bis_send_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // has valid phone -> send SMS
      e("3", "6", "output_2"), // no phone -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 331. ATTENTIVE POST-PURCHASE SMS FOLLOW-UP (8 nodes)
  // ===================================================================

  "Attentive Post-Purchase SMS Follow-Up": {
    summary:
      "Multi-touch post-purchase SMS follow-up sequence. Starts from a fulfillment webhook, verifies SMS consent, sends a shipping confirmation SMS, waits for delivery, sends a usage check-in, analyzes reply sentiment, routes to review request or support, and logs sequence performance.",
    nodes: [
      trigger("Webhook: Order fulfilled", "webhook_app", {
        source: "Shopify / Fulfillment Service",
        event: "fulfillment.created",
        howItStarts:
          "An order is fulfilled and tracking information is available for the customer's shipment",
        setup:
          "Connect Shopify fulfillments/create webhook to POST fulfillment events to the workflow URL.",
        inputFields: [],
      }),

      // 2. SMS consent verification
      aiNode(
        "2",
        "AI extract fulfillment + consent data",
        280,
        "Read this fulfillment webhook and extract: customer phone, name, email, order ID, product name, tracking number, carrier, estimated delivery date, and SMS opt-in status. Respond JSON: {customer_phone: string, customer_name: string, customer_email: string, order_id: string, product_name: string, tracking_number: string, carrier: string, est_delivery: string, sms_opted_in: string}",
        "{webhook_payload}",
        [
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier", type: "string" },
          { field: "est_delivery", as: "est_delivery", type: "string" },
          { field: "sms_opted_in", as: "sms_opted_in", type: "string" },
        ],
      ),

      // 3. Shipping confirmation SMS (Twilio)
      n("3", "sendSMS", "SMS: Shipping confirmation", 520, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message:
            "Hi {customer_name}! Your {product_name} just shipped. Track: {tracking_number} ({carrier}). Est. delivery: {est_delivery}",
        },
      }),

      // 4. Delivery delay
      n("4", "delayAction", "Wait 5 days for delivery", 760, {
        delayAction: { time: 5, timeFormat: "days" },
      }),

      // 5. Usage check-in SMS
      n("5", "sendSMS", "SMS: Usage check-in", 1000, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message:
            "Hey {customer_name}! How's your {product_name}? Reply LOVE, OK, or HELP and we'll take it from there!",
        },
      }),

      // 6. Reply sentiment condition
      n("6", "condition", "Positive sentiment (LOVE/OK)?", 1240, {
        conditionData: [
          {
            variable: "reply_sentiment",
            value: "positive",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Review/upsell SMS (positive path)
      n(
        "7",
        "sendSMS",
        "SMS: Review request + upsell",
        1480,
        {
          sendSmsDetails: {
            to: "{customer_phone}",
            message:
              "So glad you love it! Would you leave a quick review? {review_url} As a thank you, here's 10% off your next order: THANKS10",
          },
        },
        80,
      ),

      // 8. Sequence performance logging (Sheets)
      sheetsNode(
        "8",
        "Log SMS sequence performance",
        1720,
        "PostPurchaseSMS",
        '[["{order_id}","{customer_phone}","{customer_name}","{product_name}","{carrier}","{tracking_number}","{reply_sentiment}","{sms_opted_in}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // positive -> review request
      e("6", "8", "output_2"), // negative/help -> log for support follow-up
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 332. AUTO-RENEW CONFIRMATION (7 nodes)
  // ===================================================================

  "Auto-Renew Confirmation": {
    summary:
      "Sends informative renewal confirmations after Stripe auto-renews a subscription. Pulls subscription and payment details, detects plan changes, generates an AI renewal summary, sends a confirmation email, checks card expiry for upcoming issues, and logs renewal data.",
    nodes: [
      trigger("Webhook: Subscription renewed (Stripe)", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_succeeded",
        howItStarts:
          "Stripe fires a webhook when a subscription auto-renewal payment succeeds",
        setup:
          "In Stripe Dashboard -> Developers -> Webhooks, add an endpoint for invoice.payment_succeeded events pointing to your workflow URL.",
        inputFields: [],
      }),

      // 2. Subscription + payment detail pull
      aiNode(
        "2",
        "AI extract renewal details",
        280,
        "Read this Stripe renewal webhook and extract: customer email, customer name, subscription ID, plan name, billing period (monthly/yearly), amount charged, currency, payment method last 4 digits, card expiry month/year, next billing date, and whether the plan changed from the previous period. Respond JSON: {customer_email: string, customer_name: string, subscription_id: string, plan_name: string, billing_period: string, amount_charged: number, currency: string, card_last4: string, card_exp_month: number, card_exp_year: number, next_billing_date: string, plan_changed: string, previous_plan: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "subscription_id", as: "subscription_id", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "billing_period", as: "billing_period", type: "string" },
          { field: "amount_charged", as: "amount_charged", type: "number" },
          { field: "card_last4", as: "card_last4", type: "string" },
          { field: "card_exp_month", as: "card_exp_month", type: "number" },
          { field: "card_exp_year", as: "card_exp_year", type: "number" },
          {
            field: "next_billing_date",
            as: "next_billing_date",
            type: "string",
          },
          { field: "plan_changed", as: "plan_changed", type: "string" },
        ],
      ),

      // 3. Plan change detection condition
      n("3", "condition", "Plan changed since last period?", 520, {
        conditionData: [
          {
            variable: "plan_changed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI renewal summary
      aiNode(
        "4",
        "AI generate renewal summary email",
        760,
        "Write a clear, professional subscription renewal confirmation email. Include: plan name, amount charged, billing period, payment method (last 4), next billing date. If the plan changed, highlight what changed. If card expires within 60 days, add a gentle reminder to update payment method. Respond JSON: {email_subject: string, email_body: string, card_expiry_warning: string}",
        "Customer: {customer_name} | Plan: {plan_name} | Amount: {amount_charged} {currency} | Period: {billing_period} | Card: ****{card_last4} (exp {card_exp_month}/{card_exp_year}) | Next billing: {next_billing_date} | Plan changed: {plan_changed}",
        [
          { field: "email_subject", as: "renewal_subject", type: "string" },
          { field: "email_body", as: "renewal_body", type: "string" },
          {
            field: "card_expiry_warning",
            as: "card_expiry_warning",
            type: "string",
          },
        ],
      ),

      // 5. Confirmation email
      emailNode(
        "5",
        "Send renewal confirmation email",
        1000,
        "{customer_email}",
        "{renewal_subject}",
        "{renewal_body}",
      ),

      // 6. Card expiry check (condition acts as flag)
      n("6", "condition", "Card expiring within 60 days?", 1240, {
        conditionData: [
          {
            variable: "card_expiry_warning",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Renewal logging (Sheets)
      sheetsNode(
        "7",
        "Log renewal to Sheets",
        1480,
        "SubscriptionRenewals",
        '[["{customer_email}","{customer_name}","{subscription_id}","{plan_name}","{billing_period}","{amount_charged}","{card_last4}","{card_exp_month}/{card_exp_year}","{next_billing_date}","{plan_changed}","{card_expiry_warning}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // plan changed -> include change details
      e("3", "4", "output_2"), // no change -> standard renewal
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // expiring -> log with warning
      e("6", "7", "output_2"), // card OK -> standard log
    ],
  },

  // ===================================================================
  // 333. B2B ORDER PORTAL CHATBOT (9 nodes)
  // ===================================================================

  "B2B Order Portal Chatbot": {
    summary:
      "AI-powered B2B customer portal chatbot. Loads account data from Salesforce, classifies intent, routes by topic (order/pricing/RMA), retrieves relevant data, generates grounded responses, escalates complex issues to account teams on Slack, and logs all interactions.",
    nodes: [
      trigger("Webhook: B2B portal chat message", "webhook_app", {
        source: "B2B Customer Portal / Live Chat Widget",
        event: "chat.message.received",
        howItStarts:
          "A B2B customer sends a message through the order portal's chat widget",
        setup:
          "Embed the chat widget on your B2B portal and configure it to POST messages to the workflow URL. Include the customer's account ID in the payload.",
        inputFields: [],
      }),

      // 2. Account data load (Salesforce)
      n("2", "hitApi", "Load account from Salesforce", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{sf_instance}.salesforce.com/services/data/v59.0/sobjects/Account/{account_id}",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
          ],
          body: [],
          variables: [
            { name: "Name", customName: "account_name", type: "string" },
            { name: "Owner.Name", customName: "account_owner", type: "string" },
            {
              name: "Owner.Email",
              customName: "account_owner_email",
              type: "string",
            },
            {
              name: "AnnualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            { name: "Type", customName: "account_type", type: "string" },
            {
              name: "Payment_Terms__c",
              customName: "payment_terms",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI intent classification
      aiNode(
        "3",
        "AI classify B2B chat intent",
        520,
        "You are a B2B customer support classifier. Classify the customer message into one of these intents: order_status (tracking, ETA, shipment), pricing (quotes, volume discounts, contract pricing), rma (returns, exchanges, defective items), reorder (repeat a previous order), account (billing, payment terms, credit), other (general questions). Extract the specific request details. Respond JSON: {intent: string, sub_intent: string, order_number: string, product_sku: string, quantity: number, urgency: string, customer_message: string, needs_escalation: string}",
        "Account: {account_name} | Type: {account_type} | Message: {webhook_payload}",
        [
          { field: "intent", as: "chat_intent", type: "string" },
          { field: "sub_intent", as: "sub_intent", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          { field: "product_sku", as: "product_sku", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
        ],
      ),

      // 4. Intent routing condition
      n("4", "condition", "Order-related intent?", 760, {
        conditionData: [
          {
            variable: "chat_intent",
            value: "order_status",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Order/pricing/RMA data retrieval (Salesforce)
      n("5", "hitApi", "Retrieve order/pricing data from Salesforce", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://{sf_instance}.salesforce.com/services/data/v59.0/query/?q=SELECT+Id,OrderNumber,Status,TotalAmount,ShipDate+FROM+Order+WHERE+AccountId='{account_id}'+ORDER+BY+CreatedDate+DESC+LIMIT+5",
          headers: [
            { key: "Authorization", value: "Bearer {salesforce_token}" },
          ],
          body: [],
          variables: [
            {
              name: "records[0].OrderNumber",
              customName: "latest_order_number",
              type: "string",
            },
            {
              name: "records[0].Status",
              customName: "latest_order_status",
              type: "string",
            },
            {
              name: "records[0].TotalAmount",
              customName: "latest_order_amount",
              type: "number",
            },
            {
              name: "records[0].ShipDate",
              customName: "latest_ship_date",
              type: "string",
            },
            { name: "totalSize", customName: "order_count", type: "number" },
          ],
        },
      }),

      // 6. AI grounded response
      aiNode(
        "6",
        "AI generate grounded response",
        1240,
        "You are a B2B customer support agent for {account_name}. Generate a helpful, professional response based on the customer intent and retrieved data. For order_status, provide specific tracking/status info. For pricing, reference their contract terms. For RMA, outline the return process. Always be accurate — do not make up order numbers or statuses. If data is insufficient, say so and offer to connect them with their account manager. Respond JSON: {response_text: string, confidence: number, suggested_next_action: string, data_sources_used: string}",
        "Intent: {chat_intent} | Sub-intent: {sub_intent} | Account: {account_name} | Payment terms: {payment_terms} | Latest order: {latest_order_number} ({latest_order_status}) | Amount: ${latest_order_amount} | Ship date: {latest_ship_date}",
        [
          { field: "response_text", as: "bot_response", type: "string" },
          { field: "confidence", as: "response_confidence", type: "number" },
          { field: "suggested_next_action", as: "next_action", type: "string" },
        ],
      ),

      // 7. Escalation condition
      n("7", "condition", "Needs human escalation?", 1480, {
        conditionData: [
          {
            variable: "needs_escalation",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Account team Slack alert
      slackNode(
        "8",
        "Slack: Escalate to account team",
        1720,
        "B2B Chat Escalation: {account_name}\\nIntent: {chat_intent} ({sub_intent})\\nUrgency: {urgency}\\nAccount Owner: {account_owner}\\nBot confidence: {response_confidence}%\\nSuggested action: {next_action}",
      ),

      // 9. Interaction logging
      sheetsNode(
        "9",
        "Log chat interaction",
        1960,
        "B2BChatLog",
        '[["{account_name}","{chat_intent}","{sub_intent}","{urgency}","{response_confidence}","{needs_escalation}","{latest_order_number}","{latest_order_status}","{next_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // order intent -> pull order data
      e("4", "5", "output_2"), // other intent -> pull general account data
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // needs escalation -> Slack
      e("7", "9", "output_2"), // no escalation -> just log
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 334. BACK IN STOCK NOTIFICATION (7 nodes)
  // ===================================================================

  "Back in Stock Notification": {
    summary:
      "Multi-channel back-in-stock notifications. Processes restock webhooks, pulls the waitlist with channel preferences, routes by preferred channel (email/SMS/push), sends the notification, tracks buy-link clicks, logs conversions, and monitors waitlist metrics.",
    nodes: [
      trigger("Webhook: Product back in stock", "webhook_app", {
        source: "Shopify / Inventory Management",
        event: "product.restocked",
        howItStarts:
          "A product that was out of stock has been restocked and is now available for purchase",
        setup:
          "Connect your Shopify inventory webhook to POST restock events to the workflow URL. Ensure your waitlist system stores channel preferences (email, SMS, push).",
        inputFields: [],
      }),

      // 2. Waitlist pull + channel preference
      aiNode(
        "2",
        "AI extract restock + waitlist data",
        280,
        "Read this restock webhook and extract: product name, product ID, variant, price, stock quantity, product URL, image URL, waitlist subscriber email, subscriber phone, subscriber name, and preferred channel (email, sms, push). Respond JSON: {product_name: string, product_id: string, variant: string, price: number, stock_qty: number, product_url: string, subscriber_email: string, subscriber_phone: string, subscriber_name: string, preferred_channel: string}",
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "price", as: "price", type: "number" },
          { field: "stock_qty", as: "stock_qty", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
        ],
      ),

      // 3. Channel routing condition
      n("3", "condition", "Email preferred channel?", 520, {
        conditionData: [
          {
            variable: "preferred_channel",
            value: "email",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Email notification
      emailNode(
        "4",
        "Send back-in-stock email",
        760,
        "{subscriber_email}",
        "It's back! {product_name} is available again",
        "Hi {subscriber_name},\\n\\nGreat news! The {product_name} you've been waiting for is back in stock.\\n\\nPrice: ${price}\\nStock: {stock_qty} available\\n\\nGrab yours before it sells out again: {product_url}\\n\\nLimited quantities available!",
      ),

      // 5. SMS notification (fallback channel)
      n(
        "5",
        "sendSMS",
        "SMS: Back-in-stock alert",
        760,
        {
          sendSmsDetails: {
            to: "{subscriber_phone}",
            message:
              "Hi {subscriber_name}! {product_name} is back in stock at ${price}. Grab yours: {product_url}",
          },
        },
        460,
      ),

      // 6. Conversion logging (Sheets)
      sheetsNode(
        "6",
        "Log notification + conversion",
        1000,
        "BackInStockNotifications",
        '[["{product_id}","{product_name}","{price}","{stock_qty}","{subscriber_email}","{subscriber_name}","{preferred_channel}"]]',
      ),

      // 7. Product waitlist metrics
      slackNode(
        "7",
        "Slack: Waitlist fulfillment summary",
        1240,
        "Back-in-stock notifications sent for {product_name}\\nStock: {stock_qty} | Price: ${price}\\nChannel: {preferred_channel}\\nSubscriber: {subscriber_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // email preferred -> email
      e("3", "5", "output_2"), // SMS/push preferred -> SMS
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 335. BACK-IN-STOCK RECOMMENDATIONS RELATED TO PURCHASE (7 nodes)
  // ===================================================================

  "Back-in-Stock Recommendations Related to Purchase": {
    summary:
      "Sends personalized back-in-stock recommendations to customers who previously bought related products. Pulls purchase affinity data, filters by recency and ownership, generates AI-personalized recommendations, sends targeted emails, tracks clicks and purchases, and logs affinity signals.",
    nodes: [
      trigger("Webhook: Related product restocked", "webhook_app", {
        source: "Shopify / Inventory Management",
        event: "inventory.restock",
        howItStarts:
          "A product is restocked and the system identifies customers who purchased related or complementary items",
        setup:
          "Connect your Shopify inventory webhook. The workflow cross-references restocked products against purchase history to find customers with affinity signals.",
        inputFields: [],
      }),

      // 2. Purchase affinity customer pull
      aiNode(
        "2",
        "AI extract restock + affinity data",
        280,
        "Read this restock webhook and identify: the restocked product details, related product categories, complementary items, customer email and name from affinity match, their original purchase (product, date, amount), and the affinity score. Respond JSON: {restocked_product: string, restocked_price: number, product_url: string, category: string, customer_email: string, customer_name: string, original_purchase: string, purchase_date: string, purchase_amount: number, affinity_score: number, already_owns: string}",
        "{webhook_payload}",
        [
          {
            field: "restocked_product",
            as: "restocked_product",
            type: "string",
          },
          { field: "restocked_price", as: "restocked_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "original_purchase",
            as: "original_purchase",
            type: "string",
          },
          { field: "purchase_date", as: "purchase_date", type: "string" },
          { field: "affinity_score", as: "affinity_score", type: "number" },
          { field: "already_owns", as: "already_owns", type: "string" },
        ],
      ),

      // 3. Recency/ownership filter (runCode)
      n("3", "runCode", "Filter by recency and ownership", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter: skip if customer already owns the restocked item or purchase too old
const purchaseDate = new Date(vars.purchase_date);
const daysSincePurchase = Math.floor((Date.now() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));
const alreadyOwns = vars.already_owns === "true";
const affinityScore = parseFloat(vars.affinity_score) || 0;
const result = {
  should_send: !alreadyOwns && daysSincePurchase <= 365 && affinityScore >= 50 ? "true" : "false",
  days_since_purchase: daysSincePurchase,
  filter_reason: alreadyOwns ? "already_owns" : daysSincePurchase > 365 ? "too_old" : affinityScore < 50 ? "low_affinity" : "passed",
  personalization_tier: affinityScore >= 80 ? "high" : affinityScore >= 60 ? "medium" : "low"
};
return result;`,
          outputVariables: [
            { name: "should_send", type: "string" },
            { name: "days_since_purchase", type: "number" },
            { name: "filter_reason", type: "string" },
            { name: "personalization_tier", type: "string" },
          ],
        },
      }),

      // 4. AI personalized recommendation
      aiNode(
        "4",
        "AI generate personalized recommendation",
        760,
        "Write a personalized email recommending a restocked product to someone who previously bought a related item. Reference their original purchase by name, explain the connection between the products, and highlight why this would complement what they already own. Scale enthusiasm by affinity score. Respond JSON: {email_subject: string, email_body: string, connection_explanation: string}",
        "Customer: {customer_name} | Original purchase: {original_purchase} (bought {days_since_purchase} days ago) | Restocked: {restocked_product} (${restocked_price}) | Affinity: {affinity_score} | Tier: {personalization_tier} | URL: {product_url}",
        [
          { field: "email_subject", as: "rec_subject", type: "string" },
          { field: "email_body", as: "rec_body", type: "string" },
        ],
      ),

      // 5. Recommendation email
      emailNode(
        "5",
        "Send personalized recommendation email",
        1000,
        "{customer_email}",
        "{rec_subject}",
        "{rec_body}",
      ),

      // 6. Click/purchase tracking
      sheetsNode(
        "6",
        "Log recommendation + tracking",
        1240,
        "AffinityRecommendations",
        '[["{customer_email}","{customer_name}","{original_purchase}","{restocked_product}","{restocked_price}","{affinity_score}","{personalization_tier}","{days_since_purchase}","{filter_reason}"]]',
      ),

      // 7. Affinity signal logging (Sheets)
      slackNode(
        "7",
        "Slack: Affinity recommendation sent",
        1480,
        "Affinity recommendation sent: {restocked_product} -> {customer_name}\\nOriginal purchase: {original_purchase} ({days_since_purchase} days ago)\\nAffinity: {affinity_score} | Tier: {personalization_tier}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 336. BACKORDER NOTIFICATION (7 nodes)
  // ===================================================================

  "Backorder Notification": {
    summary:
      "Sends transparent backorder notifications when items cannot be immediately fulfilled. Detects backorders, pulls restock dates and substitutes, generates AI-crafted delay messages, routes by partial shipment eligibility, emails customers with options, updates Shopify order tags, and tracks customer choices.",
    nodes: [
      trigger("Webhook: Backorder detected", "webhook_app", {
        source: "Shopify / Fulfillment / Warehouse",
        event: "order.backorder.detected",
        howItStarts:
          "An order item is detected as backordered during fulfillment processing due to insufficient inventory",
        setup:
          "Connect your fulfillment system or warehouse management to POST backorder events (order ID, backordered SKU, estimated restock date) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Restock date + substitute pull
      aiNode(
        "2",
        "AI extract backorder details",
        280,
        "Read this backorder webhook and extract: customer email, customer name, order ID, backordered product name, backordered SKU, original quantity, available quantity, estimated restock date, suggested substitute product name and price, can ship partial order (true/false). Respond JSON: {customer_email: string, customer_name: string, order_id: string, product_name: string, sku: string, ordered_qty: number, available_qty: number, restock_date: string, substitute_name: string, substitute_price: number, can_partial_ship: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "sku", as: "sku", type: "string" },
          { field: "ordered_qty", as: "ordered_qty", type: "number" },
          { field: "available_qty", as: "available_qty", type: "number" },
          { field: "restock_date", as: "restock_date", type: "string" },
          { field: "substitute_name", as: "substitute_name", type: "string" },
          { field: "can_partial_ship", as: "can_partial_ship", type: "string" },
        ],
      ),

      // 3. AI transparent delay message
      aiNode(
        "3",
        "AI generate backorder notification",
        520,
        "Write a transparent, empathetic backorder notification email. Be upfront about the delay, provide the expected restock date, offer alternatives (partial shipment if available, substitute product, or wait). Include a discount code (10% off) as an apology. Keep it honest and solution-oriented. Respond JSON: {email_subject: string, email_body: string, apology_discount_code: string, options_offered: string}",
        "Customer: {customer_name} | Order: {order_id} | Product: {product_name} | Ordered: {ordered_qty} | Available: {available_qty} | Restock: {restock_date} | Substitute: {substitute_name} | Can partial ship: {can_partial_ship}",
        [
          { field: "email_subject", as: "backorder_subject", type: "string" },
          { field: "email_body", as: "backorder_body", type: "string" },
          {
            field: "apology_discount_code",
            as: "apology_code",
            type: "string",
          },
          { field: "options_offered", as: "options_offered", type: "string" },
        ],
      ),

      // 4. Partial shipment condition
      n("4", "condition", "Partial shipment available?", 760, {
        conditionData: [
          {
            variable: "can_partial_ship",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Customer options email
      emailNode(
        "5",
        "Send backorder notification email",
        1000,
        "{customer_email}",
        "{backorder_subject}",
        "{backorder_body}",
      ),

      // 6. Order tag update (Shopify)
      shopifyNode(
        "6",
        "Tag order as backordered in Shopify",
        1240,
        "PUT",
        "orders/{order_id}.json",
        [
          {
            key: "order",
            value: '{"tags":"backordered,restock-{restock_date}"}',
          },
        ],
        [
          { name: "order.id", customName: "tagged_order_id", type: "string" },
          { name: "order.tags", customName: "order_tags", type: "string" },
        ],
      ),

      // 7. Choice tracking (Sheets)
      sheetsNode(
        "7",
        "Log backorder notification",
        1480,
        "BackorderTracker",
        '[["{order_id}","{customer_email}","{customer_name}","{product_name}","{sku}","{ordered_qty}","{available_qty}","{restock_date}","{substitute_name}","{can_partial_ship}","{options_offered}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // partial ship available -> email with options
      e("4", "5", "output_2"), // no partial -> email with wait/substitute
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 337. BEST-SELLERS REACTIVATION (8 nodes)
  // ===================================================================

  "Best-Sellers Reactivation": {
    summary:
      "Reactivates lapsed customers with personalized best-seller recommendations. Scans weekly for lapsed customers, pulls best-seller and inventory data, generates AI taste-matched recommendations, assigns value-tier offers, creates unique discount codes, sends reactivation emails, tracks conversions, and logs winning patterns.",
    nodes: [
      trigger("Schedule: Weekly lapsed customer reactivation", "schedule", {
        schedule: "Every Monday at 10:00 AM",
        howItStarts:
          "Runs weekly to identify lapsed customers (no purchase in 60-180 days) and send them personalized best-seller recommendations",
        setup:
          "Activate this workflow and connect your Shopify store and email service. The workflow identifies lapsed customers and matches them with current best-sellers.",
        inputFields: [],
      }),

      // 2. Best-seller + inventory pull
      shopifyNode(
        "2",
        "Pull best-sellers with inventory",
        280,
        "GET",
        "products.json?limit=20&sort_key=best_selling&fields=id,title,body_html,variants,images,tags",
        [],
        [
          {
            name: "products[0].title",
            customName: "bestseller_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "bestseller_1_price",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "bestseller_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "bestseller_2_price",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "bestseller_3",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "bestseller_3_price",
            type: "string",
          },
        ],
      ),

      // 3. AI taste-matched recommendations
      aiNode(
        "3",
        "AI match best-sellers to lapsed customer",
        520,
        "You are a retention marketing specialist. Given a lapsed customer's purchase history and current best-sellers, select the 3 most relevant products for them based on taste affinity. Write a warm win-back email that acknowledges their absence, highlights what is new and trending, and presents the matched recommendations. Assign value tier: dormant (60-90 days), at_risk (90-150 days), churned (150+ days). Respond JSON: {email_subject: string, email_body: string, matched_products: string, value_tier: string, days_lapsed: number, customer_email: string, customer_name: string, last_purchase_category: string}",
        "Best-sellers: {bestseller_1} (${bestseller_1_price}), {bestseller_2} (${bestseller_2_price}), {bestseller_3} (${bestseller_3_price}) | Customer purchase history from Shopify",
        [
          {
            field: "email_subject",
            as: "reactivation_subject",
            type: "string",
          },
          { field: "email_body", as: "reactivation_body", type: "string" },
          { field: "value_tier", as: "lapse_tier", type: "string" },
          { field: "days_lapsed", as: "days_lapsed", type: "number" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
        ],
      ),

      // 4. Value-tier offer assignment (condition)
      n("4", "condition", "Churned tier (150+ days)?", 760, {
        conditionData: [
          {
            variable: "days_lapsed",
            value: "150",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Unique code generation (runCode)
      n("5", "runCode", "Generate unique reactivation discount code", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate tier-based reactivation discount code
const tier = vars.lapse_tier;
const discounts = { dormant: 10, at_risk: 15, churned: 25 };
const discount = discounts[tier] || 10;
const random = Math.random().toString(36).substring(2, 8).toUpperCase();
const code = "WEBACK" + discount + "-" + random;
const result = {
  discount_code: code,
  discount_percent: discount,
  offer_expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  free_shipping: tier === "churned" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "discount_percent", type: "number" },
            { name: "offer_expires", type: "string" },
            { name: "free_shipping", type: "string" },
          ],
        },
      }),

      // 6. Reactivation email
      emailNode(
        "6",
        "Send reactivation email",
        1240,
        "{customer_email}",
        "{reactivation_subject}",
        "{reactivation_body}\\n\\nYour exclusive comeback offer: {discount_percent}% off with code {discount_code}\\nExpires: {offer_expires}\\n" +
          "Free shipping: {free_shipping}",
      ),

      // 7. Conversion tracking
      sheetsNode(
        "7",
        "Log reactivation campaign",
        1480,
        "ReactivationCampaigns",
        '[["{customer_email}","{customer_name}","{days_lapsed}","{lapse_tier}","{discount_code}","{discount_percent}","{free_shipping}","{bestseller_1}","{bestseller_2}","{bestseller_3}"]]',
      ),

      // 8. Winning pattern logging (Sheets)
      slackNode(
        "8",
        "Slack: Reactivation batch summary",
        1720,
        "Weekly reactivation sent to {customer_name} ({customer_email})\\nLapsed: {days_lapsed} days ({lapse_tier})\\nOffer: {discount_percent}% off + free shipping: {free_shipping}\\nCode: {discount_code}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // churned -> high discount
      e("4", "5", "output_2"), // dormant/at_risk -> standard discount
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 338. BIRTHDAY PLUS PURCHASE-HISTORY HYBRID OFFER (8 nodes)
  // ===================================================================

  "Birthday Plus Purchase-History Hybrid Offer": {
    summary:
      "Sends hyper-personalized birthday offers combining birthday celebration with purchase history insights. Scans daily for birthdays, hydrates purchase history and affinity data, generates AI-curated picks, scales offers by value tier, creates unique birthday codes, sends birthday emails, tracks redemptions, and compares AOV.",
    nodes: [
      trigger("Schedule: Daily birthday scan", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to identify customers with birthdays today and generate personalized birthday offers based on their purchase history",
        setup:
          "Activate this workflow and connect your CRM, Shopify, and email service. Ensure customer birth dates are stored in your CRM.",
        inputFields: [],
      }),

      // 2. Purchase history + affinity hydration
      n("2", "hitApi", "Pull birthday customers + purchase history", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"date_of_birth_month_day","operator":"EQ","value":"{today_month_day}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","date_of_birth","total_revenue","favorite_category","last_purchase_product","purchase_count","average_order_value"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "lifetime_value",
              type: "number",
            },
            {
              name: "results[0].properties.favorite_category",
              customName: "favorite_category",
              type: "string",
            },
            {
              name: "results[0].properties.last_purchase_product",
              customName: "last_purchase",
              type: "string",
            },
            {
              name: "results[0].properties.purchase_count",
              customName: "purchase_count",
              type: "number",
            },
            {
              name: "results[0].properties.average_order_value",
              customName: "avg_order_value",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI curated picks + message
      aiNode(
        "3",
        "AI curate birthday picks + message",
        520,
        "Write an exciting, personalized birthday email. Reference the customer by name, celebrate their special day, and recommend 3 products based on their favorite category and purchase history. Scale the enthusiasm and offer based on their loyalty: low LTV (<$100) = 15% off, medium ($100-$500) = 20% off + free gift, high (>$500) = 25% off + free gift + free shipping. Respond JSON: {email_subject: string, email_body: string, curated_picks: string, discount_percent: number, bonus_gift: string, value_tier: string}",
        "Customer: {first_name} | Email: {customer_email} | LTV: ${lifetime_value} | Favorite: {favorite_category} | Last purchase: {last_purchase} | Orders: {purchase_count} | AOV: ${avg_order_value}",
        [
          { field: "email_subject", as: "birthday_subject", type: "string" },
          { field: "email_body", as: "birthday_body", type: "string" },
          { field: "curated_picks", as: "curated_picks", type: "string" },
          {
            field: "discount_percent",
            as: "birthday_discount",
            type: "number",
          },
          { field: "bonus_gift", as: "bonus_gift", type: "string" },
          { field: "value_tier", as: "value_tier", type: "string" },
        ],
      ),

      // 4. Value-tier offer scaling (condition)
      n("4", "condition", "High-value customer (LTV > $500)?", 760, {
        conditionData: [
          {
            variable: "lifetime_value",
            value: "500",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Unique birthday code (runCode)
      n("5", "runCode", "Generate unique birthday discount code", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate unique birthday discount code
const discount = parseInt(vars.birthday_discount) || 15;
const random = Math.random().toString(36).substring(2, 7).toUpperCase();
const code = "BDAY" + discount + "-" + random;
const result = {
  birthday_code: code,
  code_value: discount + "% off",
  expiry: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
  includes_gift: vars.bonus_gift && vars.bonus_gift !== "none" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "birthday_code", type: "string" },
            { name: "code_value", type: "string" },
            { name: "expiry", type: "string" },
            { name: "includes_gift", type: "string" },
          ],
        },
      }),

      // 6. Birthday email send
      emailNode(
        "6",
        "Send birthday celebration email",
        1240,
        "{customer_email}",
        "{birthday_subject}",
        "{birthday_body}\\n\\nYour birthday gift: {code_value} with code {birthday_code}\\nExpires: {expiry}\\nBonus: {bonus_gift}",
      ),

      // 7. Redemption tracking
      sheetsNode(
        "7",
        "Log birthday campaign",
        1480,
        "BirthdayCampaigns",
        '[["{customer_email}","{first_name}","{lifetime_value}","{value_tier}","{birthday_code}","{birthday_discount}","{bonus_gift}","{curated_picks}","{favorite_category}","{avg_order_value}"]]',
      ),

      // 8. AOV comparison logging (Sheets)
      slackNode(
        "8",
        "Slack: Birthday campaign sent",
        1720,
        "Birthday email sent to {first_name} ({customer_email})\\nLTV: ${lifetime_value} | Tier: {value_tier}\\nOffer: {code_value} + {bonus_gift}\\nCode: {birthday_code} (expires {expiry})\\nPicks: {curated_picks}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high value -> premium offer
      e("4", "5", "output_2"), // standard -> basic offer
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 339. BROWSE ABANDONMENT FOR BACK-IN-STOCK WAITLIST JOINERS (7 nodes)
  // ===================================================================

  "Browse Abandonment for Back-in-Stock Waitlist Joiners": {
    summary:
      "Engages waitlist joiners who browse but leave without alternatives. Processes waitlist join webhooks, pulls OOS product and alternative data, generates AI substitute recommendations, checks restock timeline, sends follow-up emails with alternatives, tracks purchases, and logs alternative vs wait behavior.",
    nodes: [
      trigger("Webhook: Waitlist join event", "webhook_app", {
        source: "Shopify / Waitlist App",
        event: "waitlist.joined",
        howItStarts:
          "A customer joins the back-in-stock waitlist for an out-of-stock product and then continues browsing without purchasing alternatives",
        setup:
          "Connect your waitlist app (Back in Stock, Klaviyo BIS, etc.) to POST waitlist join events to the workflow URL.",
        inputFields: [],
      }),

      // 2. OOS product + alternative pull
      aiNode(
        "2",
        "AI extract waitlist + browsing data",
        280,
        "Read this waitlist join webhook and extract: customer email, customer name, waitlisted product name, product price, product category, estimated restock date, alternative products browsed (if any), customer browse history after waitlist join. Respond JSON: {customer_email: string, customer_name: string, waitlisted_product: string, product_price: number, product_category: string, restock_estimate: string, alternatives_browsed: string, product_url: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "waitlisted_product",
            as: "waitlisted_product",
            type: "string",
          },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "restock_estimate", as: "restock_estimate", type: "string" },
          {
            field: "alternatives_browsed",
            as: "alternatives_browsed",
            type: "string",
          },
        ],
      ),

      // 3. AI substitute recommendation
      aiNode(
        "3",
        "AI recommend available substitutes",
        520,
        "The customer is waitlisted for an out-of-stock item. Recommend 2-3 available alternatives in the same category and price range. Acknowledge their interest in the waitlisted product, reassure them they are on the list, and present alternatives as options to consider while they wait. Be helpful, not pushy. Respond JSON: {email_subject: string, email_body: string, substitute_1: string, substitute_2: string, substitute_3: string, recommendation_strategy: string}",
        "Customer: {customer_name} | Waitlisted: {waitlisted_product} (${product_price}) | Category: {product_category} | Restock: {restock_estimate} | Browsed alternatives: {alternatives_browsed}",
        [
          { field: "email_subject", as: "waitlist_subject", type: "string" },
          { field: "email_body", as: "waitlist_body", type: "string" },
          { field: "substitute_1", as: "substitute_1", type: "string" },
          { field: "substitute_2", as: "substitute_2", type: "string" },
          {
            field: "recommendation_strategy",
            as: "rec_strategy",
            type: "string",
          },
        ],
      ),

      // 4. Restock timeline condition
      n("4", "condition", "Restock within 7 days?", 760, {
        conditionData: [
          {
            variable: "restock_estimate",
            value: "7_days",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Follow-up email with alternatives
      emailNode(
        "5",
        "Send waitlist follow-up with alternatives",
        1000,
        "{customer_email}",
        "{waitlist_subject}",
        "{waitlist_body}",
      ),

      // 6. Purchase tracking
      sheetsNode(
        "6",
        "Log waitlist alternative engagement",
        1240,
        "WaitlistAlternatives",
        '[["{customer_email}","{customer_name}","{waitlisted_product}","{product_price}","{restock_estimate}","{substitute_1}","{substitute_2}","{rec_strategy}"]]',
      ),

      // 7. Alternative vs wait logging (Sheets)
      slackNode(
        "7",
        "Slack: Waitlist follow-up sent",
        1480,
        "Waitlist follow-up sent to {customer_name} ({customer_email})\\nWaitlisted: {waitlisted_product} (${product_price})\\nRestock: {restock_estimate}\\nAlternatives: {substitute_1}, {substitute_2}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // restocking soon -> emphasize wait
      e("4", "5", "output_2"), // long wait -> push alternatives harder
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 340. BROWSE ABANDONMENT FOR OUT-OF-STOCK SUBSTITUTE (7 nodes)
  // ===================================================================

  "Browse Abandonment for Out-of-Stock Substitute": {
    summary:
      "Recovers browsers who left after viewing an out-of-stock product. Detects OOS PDP exits, pulls browsed product attributes, uses AI similarity matching to find substitutes, checks substitute quality, sends side-by-side comparison emails, tracks conversions, and logs substitute effectiveness.",
    nodes: [
      trigger("Webhook: OOS product page exit", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.oos_exit",
        howItStarts:
          "A visitor exits after viewing a product detail page (PDP) for a product that is currently out of stock",
        setup:
          "Configure your site tracking to detect exits from out-of-stock PDPs and POST the browsing data (viewed product, visitor identity if known, session data) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Browsed product attribute pull
      aiNode(
        "2",
        "AI extract OOS browse data",
        280,
        "Read this OOS browse abandonment event and extract: visitor email (if known), visitor name, OOS product name, product category, product price, key attributes (color, size, material, brand), time on page, and session pages viewed. Respond JSON: {visitor_email: string, visitor_name: string, oos_product: string, product_category: string, product_price: number, key_attributes: string, brand: string, time_on_page: number, pages_viewed: number}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "oos_product", as: "oos_product", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "key_attributes", as: "key_attributes", type: "string" },
          { field: "brand", as: "brand", type: "string" },
        ],
      ),

      // 3. AI similarity matching
      aiNode(
        "3",
        "AI find similar in-stock substitutes",
        520,
        "Find the best in-stock substitute products for an out-of-stock item. Match on: same category, similar price (+/- 20%), matching key attributes (color, size, material where possible), comparable quality/brand tier. Rate match quality 0-100. Respond JSON: {substitute_1_name: string, substitute_1_price: number, substitute_1_match_score: number, substitute_2_name: string, substitute_2_price: number, substitute_2_match_score: number, best_match_score: number, comparison_highlights: string}",
        "OOS: {oos_product} | Category: {product_category} | Price: ${product_price} | Attributes: {key_attributes} | Brand: {brand}",
        [
          { field: "substitute_1_name", as: "sub_1_name", type: "string" },
          { field: "substitute_1_price", as: "sub_1_price", type: "number" },
          {
            field: "substitute_1_match_score",
            as: "sub_1_score",
            type: "number",
          },
          { field: "substitute_2_name", as: "sub_2_name", type: "string" },
          { field: "substitute_2_price", as: "sub_2_price", type: "number" },
          { field: "best_match_score", as: "best_match_score", type: "number" },
          {
            field: "comparison_highlights",
            as: "comparison_highlights",
            type: "string",
          },
        ],
      ),

      // 4. Strong substitute condition
      n("4", "condition", "Strong substitute found (score > 70)?", 760, {
        conditionData: [
          {
            variable: "best_match_score",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Side-by-side email
      emailNode(
        "5",
        "Send substitute recommendation email",
        1000,
        "{visitor_email}",
        "Still looking for {oos_product}? Check out these alternatives",
        "Hi {visitor_name},\\n\\nWe noticed you were looking at {oos_product}, which is currently out of stock. We found some great alternatives:\\n\\n{comparison_highlights}\\n\\nTop match: {sub_1_name} (${sub_1_price}) - {sub_1_score}% match\\nAlso consider: {sub_2_name} (${sub_2_price})\\n\\nShop now while they're in stock!",
      ),

      // 6. Click/purchase tracking
      sheetsNode(
        "6",
        "Log substitute recommendation",
        1240,
        "OOSSubstitutes",
        '[["{visitor_email}","{oos_product}","{product_price}","{product_category}","{sub_1_name}","{sub_1_price}","{sub_1_score}","{sub_2_name}","{sub_2_price}","{best_match_score}"]]',
      ),

      // 7. Substitute quality logging (Sheets)
      slackNode(
        "7",
        "Slack: OOS substitute sent",
        1480,
        "OOS substitute email sent to {visitor_name}\\nOOS: {oos_product} (${product_price})\\nBest match: {sub_1_name} (${sub_1_price}) at {sub_1_score}% match\\nAlternative: {sub_2_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // strong match -> send comparison
      e("4", "6", "output_2"), // weak match -> just log
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 341. BROWSE ABANDONMENT WITH QUIZ RECOMMENDATION (7 nodes)
  // ===================================================================

  "Browse Abandonment with Quiz Recommendation": {
    summary:
      "Helps indecisive browsers by offering a product recommendation quiz. Detects decision paralysis, analyzes the session, infers barriers with AI, selects the right quiz type, sends an invitation email, tracks quiz completion, and logs post-quiz purchase behavior.",
    nodes: [
      trigger("Webhook: Decision paralysis detected", "webhook_app", {
        source: "Website Analytics / Behavior Tracking",
        event: "browse.decision_paralysis",
        howItStarts:
          "A visitor shows signs of decision paralysis: viewed 5+ products in the same category without adding to cart, or spent 10+ minutes on comparison pages",
        setup:
          "Configure your behavior tracking to detect high-browse/low-action patterns and POST session data (products viewed, time spent, category, visitor identity) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Session analysis (runCode)
      n("2", "runCode", "Analyze browse session patterns", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze session for decision paralysis signals
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const productsViewed = data.products_viewed || [];
const timeSpent = data.total_time_seconds || 0;
const category = data.primary_category || "unknown";
const result = {
  products_viewed_count: productsViewed.length || 0,
  time_spent_minutes: Math.round(timeSpent / 60),
  primary_category: category,
  visitor_email: data.email || "",
  visitor_name: data.name || "there",
  paralysis_score: Math.min(100, (productsViewed.length * 10) + Math.round(timeSpent / 30)),
  browsed_price_range: productsViewed.length > 0 ? JSON.stringify({ min: Math.min(...productsViewed.map(p => p.price || 0)), max: Math.max(...productsViewed.map(p => p.price || 0)) }) : "{}"
};
return result;`,
          outputVariables: [
            { name: "products_viewed_count", type: "number" },
            { name: "time_spent_minutes", type: "number" },
            { name: "primary_category", type: "string" },
            { name: "visitor_email", type: "string" },
            { name: "visitor_name", type: "string" },
            { name: "paralysis_score", type: "number" },
            { name: "browsed_price_range", type: "string" },
          ],
        },
      }),

      // 3. AI barrier inference
      aiNode(
        "3",
        "AI infer decision barriers",
        520,
        "Analyze this browse session to infer why the customer can't decide. Common barriers: too many options (need filtering), unclear differences between products, price sensitivity (browsing range), feature comparison needed, size/fit uncertainty, lack of social proof. Based on the barrier, recommend the best quiz type: style_quiz, needs_assessment, budget_finder, comparison_tool, fit_finder. Write a short, helpful quiz invitation. Respond JSON: {inferred_barrier: string, quiz_type: string, quiz_title: string, email_subject: string, email_body: string, quiz_url: string}",
        "Category: {primary_category} | Products viewed: {products_viewed_count} | Time: {time_spent_minutes} min | Price range: {browsed_price_range} | Paralysis score: {paralysis_score}",
        [
          { field: "inferred_barrier", as: "inferred_barrier", type: "string" },
          { field: "quiz_type", as: "quiz_type", type: "string" },
          { field: "quiz_title", as: "quiz_title", type: "string" },
          { field: "email_subject", as: "quiz_subject", type: "string" },
          { field: "email_body", as: "quiz_body", type: "string" },
          { field: "quiz_url", as: "quiz_url", type: "string" },
        ],
      ),

      // 4. Quiz type selection (condition for routing)
      n("4", "condition", "High paralysis score (> 60)?", 760, {
        conditionData: [
          {
            variable: "paralysis_score",
            value: "60",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Quiz invitation email
      emailNode(
        "5",
        "Send quiz invitation email",
        1000,
        "{visitor_email}",
        "{quiz_subject}",
        "{quiz_body}",
      ),

      // 6. Quiz completion tracking
      sheetsNode(
        "6",
        "Log quiz invitation",
        1240,
        "QuizRecommendations",
        '[["{visitor_email}","{visitor_name}","{primary_category}","{products_viewed_count}","{time_spent_minutes}","{paralysis_score}","{inferred_barrier}","{quiz_type}","{quiz_title}"]]',
      ),

      // 7. Post-quiz purchase logging (Sheets)
      slackNode(
        "7",
        "Slack: Quiz recommendation sent",
        1480,
        "Quiz recommendation sent to {visitor_name} ({visitor_email})\\nCategory: {primary_category} | Viewed: {products_viewed_count} products | {time_spent_minutes} min\\nBarrier: {inferred_barrier}\\nQuiz: {quiz_title} ({quiz_type})\\nParalysis score: {paralysis_score}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high paralysis -> send quiz
      e("4", "6", "output_2"), // low paralysis -> just log
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 342. BROWSE RECOVERY AFTER SEARCH WITH NO PDP (7 nodes)
  // ===================================================================

  "Browse Recovery After Search with No PDP": {
    summary:
      "Recovers customers who searched but never clicked a product page. Detects search-no-click patterns, pulls query and results data, uses AI to correct intent, checks for better results, sends recovery emails with improved matches, tracks conversions, and logs search improvement insights.",
    nodes: [
      trigger("Webhook: Search with no PDP click", "webhook_app", {
        source: "Site Search / Analytics",
        event: "search.no_click",
        howItStarts:
          "A visitor performs a search on your store but does not click any product result, suggesting the results did not match their intent",
        setup:
          "Configure your site search analytics to detect searches with zero product clicks and POST the search data (query, results shown, visitor identity) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Query + results pull
      aiNode(
        "2",
        "AI extract search session data",
        280,
        "Read this search-no-click event and extract: visitor email, visitor name, search query, number of results shown, top 3 result titles and prices, time spent on results page, and any refinement queries attempted. Respond JSON: {visitor_email: string, visitor_name: string, search_query: string, results_shown: number, result_1: string, result_2: string, result_3: string, refinement_queries: string, time_on_results: number}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
          { field: "results_shown", as: "results_shown", type: "number" },
          { field: "result_1", as: "result_1", type: "string" },
          { field: "result_2", as: "result_2", type: "string" },
          { field: "result_3", as: "result_3", type: "string" },
          {
            field: "refinement_queries",
            as: "refinement_queries",
            type: "string",
          },
        ],
      ),

      // 3. AI corrected intent
      aiNode(
        "3",
        "AI correct search intent + find matches",
        520,
        "Analyze this failed search: the customer searched but clicked nothing. Infer what they actually wanted. Consider: misspellings, alternate product names, category-level vs specific-product intent, brand vs generic searches. Suggest 3 products that likely match their true intent. Write a recovery email. Respond JSON: {corrected_intent: string, email_subject: string, email_body: string, match_1_name: string, match_1_price: number, match_2_name: string, match_2_price: number, match_3_name: string, match_3_price: number, intent_confidence: number, search_issue: string}",
        "Query: {search_query} | Results shown: {results_shown} | Top results: {result_1}, {result_2}, {result_3} | Refinements: {refinement_queries}",
        [
          { field: "corrected_intent", as: "corrected_intent", type: "string" },
          { field: "email_subject", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "match_1_name", as: "match_1", type: "string" },
          { field: "match_2_name", as: "match_2", type: "string" },
          {
            field: "intent_confidence",
            as: "intent_confidence",
            type: "number",
          },
          { field: "search_issue", as: "search_issue", type: "string" },
        ],
      ),

      // 4. Better results condition
      n("4", "condition", "High-confidence match found (> 70)?", 760, {
        conditionData: [
          {
            variable: "intent_confidence",
            value: "70",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Recovery email with improved results
      emailNode(
        "5",
        "Send search recovery email",
        1000,
        "{visitor_email}",
        "{recovery_subject}",
        "{recovery_body}",
      ),

      // 6. Conversion tracking
      sheetsNode(
        "6",
        "Log search recovery attempt",
        1240,
        "SearchRecovery",
        '[["{visitor_email}","{search_query}","{corrected_intent}","{results_shown}","{intent_confidence}","{search_issue}","{match_1}","{match_2}"]]',
      ),

      // 7. Search improvement logging (Sheets)
      slackNode(
        "7",
        "Slack: Search recovery insight",
        1480,
        "Search recovery email sent to {visitor_name}\\nOriginal query: {search_query}\\nCorrected intent: {corrected_intent}\\nIssue: {search_issue}\\nConfidence: {intent_confidence}%\\nTop match: {match_1}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> send recovery
      e("4", "6", "output_2"), // low confidence -> just log for search improvement
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 343. BROWSE RECOVERY SPLIT BY BRAND OR CATEGORY (8 nodes)
  // ===================================================================

  "Browse Recovery Split by Brand or Category": {
    summary:
      "Splits browse recovery by whether the customer was brand-focused or category-focused. Detects abandonment, analyzes session patterns, routes by brand vs category dominance, generates brand storytelling or category buying guide content, sends segmented recovery emails, tracks path performance, and logs segmentation learnings.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / Behavior Tracking",
        event: "browse.abandoned",
        howItStarts:
          "A visitor browses multiple products without adding to cart and leaves the site",
        setup:
          "Configure your behavior tracking to detect browse abandonment and POST session data (products viewed, brands, categories, visitor identity) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Session analysis (runCode)
      n("2", "runCode", "Analyze brand vs category browsing", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze whether browsing was brand-focused or category-focused
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const products = data.products_viewed || [];
const brands = {};
const categories = {};
products.forEach(p => {
  brands[p.brand || "unknown"] = (brands[p.brand || "unknown"] || 0) + 1;
  categories[p.category || "unknown"] = (categories[p.category || "unknown"] || 0) + 1;
});
const topBrand = Object.entries(brands).sort((a,b) => b[1] - a[1])[0] || ["unknown", 0];
const topCategory = Object.entries(categories).sort((a,b) => b[1] - a[1])[0] || ["unknown", 0];
const brandConcentration = products.length > 0 ? topBrand[1] / products.length : 0;
const categoryConcentration = products.length > 0 ? topCategory[1] / products.length : 0;
const result = {
  dominant_pattern: brandConcentration > categoryConcentration ? "brand" : "category",
  top_brand: topBrand[0],
  brand_concentration: Math.round(brandConcentration * 100),
  top_category: topCategory[0],
  category_concentration: Math.round(categoryConcentration * 100),
  products_viewed: products.length,
  visitor_email: data.email || "",
  visitor_name: data.name || "there"
};
return result;`,
          outputVariables: [
            { name: "dominant_pattern", type: "string" },
            { name: "top_brand", type: "string" },
            { name: "brand_concentration", type: "number" },
            { name: "top_category", type: "string" },
            { name: "category_concentration", type: "number" },
            { name: "products_viewed", type: "number" },
            { name: "visitor_email", type: "string" },
            { name: "visitor_name", type: "string" },
          ],
        },
      }),

      // 3. Brand vs category dominance condition
      n("3", "condition", "Brand-dominant browsing?", 520, {
        conditionData: [
          {
            variable: "dominant_pattern",
            value: "brand",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI brand storytelling path
      aiNode(
        "4",
        "AI generate brand storytelling recovery",
        760,
        "The customer was focused on a specific brand. Write a brand-storytelling recovery email: share the brand's story, values, and what makes it special. Recommend the brand's top 3 products and include a brand-specific incentive. Respond JSON: {email_subject: string, email_body: string, brand_story_hook: string, top_picks: string}",
        "Brand: {top_brand} | Concentration: {brand_concentration}% | Products viewed: {products_viewed} | Customer: {visitor_name}",
        [
          { field: "email_subject", as: "brand_subject", type: "string" },
          { field: "email_body", as: "brand_body", type: "string" },
          { field: "brand_story_hook", as: "brand_hook", type: "string" },
          { field: "top_picks", as: "brand_picks", type: "string" },
        ],
      ),

      // 5. AI category buying guide path
      aiNode(
        "5",
        "AI generate category buying guide recovery",
        760,
        "The customer was browsing across brands in a category. Write a category buying guide recovery email: help them understand key differences between options, include a quick comparison chart, and recommend the best option for different needs (best value, best quality, most popular). Respond JSON: {email_subject: string, email_body: string, buying_guide_hook: string, comparison_summary: string}",
        "Category: {top_category} | Concentration: {category_concentration}% | Products viewed: {products_viewed} | Customer: {visitor_name}",
        [
          { field: "email_subject", as: "category_subject", type: "string" },
          { field: "email_body", as: "category_body", type: "string" },
          { field: "buying_guide_hook", as: "guide_hook", type: "string" },
          { field: "comparison_summary", as: "comparison", type: "string" },
        ],
        460,
      ),

      // 6. Segmented recovery email (brand path)
      emailNode(
        "6",
        "Send segmented recovery email",
        1000,
        "{visitor_email}",
        "{brand_subject}",
        "{brand_body}",
      ),

      // 7. Path performance tracking
      sheetsNode(
        "7",
        "Log browse recovery segmentation",
        1240,
        "BrowseRecoverySegments",
        '[["{visitor_email}","{visitor_name}","{dominant_pattern}","{top_brand}","{brand_concentration}","{top_category}","{category_concentration}","{products_viewed}"]]',
      ),

      // 8. Segmentation learning logging (Sheets)
      slackNode(
        "8",
        "Slack: Browse recovery sent",
        1480,
        "Browse recovery ({dominant_pattern} path) sent to {visitor_name}\\nBrand: {top_brand} ({brand_concentration}%) | Category: {top_category} ({category_concentration}%)\\nProducts viewed: {products_viewed}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // brand dominant -> brand storytelling
      e("3", "5", "output_2"), // category dominant -> buying guide
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 344. BROWSE RECOVERY SPLIT BY CUSTOMER VS PROSPECT (8 nodes)
  // ===================================================================

  "Browse Recovery Split by Customer vs Prospect": {
    summary:
      "Personalizes browse recovery based on whether the visitor is an existing customer or new prospect. Resolves identity, routes by customer vs prospect, generates loyalty-aware or trust-building messaging, delivers personalized recommendations, sends segmented emails, and updates lifecycle state with logging.",
    nodes: [
      trigger("Webhook: Browse abandonment event", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "browse.abandoned",
        howItStarts:
          "A visitor abandons their browsing session after viewing products without adding to cart",
        setup:
          "Connect your website analytics or CDP to POST browse abandonment events with visitor identity data (email, cookie ID, products viewed) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Customer identity resolution
      aiNode(
        "2",
        "AI resolve visitor identity",
        280,
        "Read this browse abandonment event and determine if the visitor is an existing customer or new prospect. Extract: email, name, customer ID (if exists), products viewed, browse session details, previous purchase count, last purchase date, lifetime value. Respond JSON: {visitor_email: string, visitor_name: string, customer_id: string, is_existing_customer: string, products_viewed: string, previous_purchases: number, last_purchase_date: string, lifetime_value: number, top_viewed_product: string, top_viewed_price: number}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "is_existing_customer", as: "is_customer", type: "string" },
          { field: "products_viewed", as: "products_viewed", type: "string" },
          {
            field: "previous_purchases",
            as: "previous_purchases",
            type: "number",
          },
          { field: "lifetime_value", as: "lifetime_value", type: "number" },
          { field: "top_viewed_product", as: "top_product", type: "string" },
          { field: "top_viewed_price", as: "top_price", type: "number" },
        ],
      ),

      // 3. Customer vs prospect condition
      n("3", "condition", "Existing customer?", 520, {
        conditionData: [
          {
            variable: "is_customer",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI loyalty-aware messaging (customer path)
      aiNode(
        "4",
        "AI generate loyalty-aware recovery",
        760,
        "Write a browse recovery email for an existing customer. Reference their loyalty: purchase count, how long they've been a customer, and their favorite categories. Recommend the browsed products with a loyalty-exclusive perk (early access, member pricing, bonus points). Be warm and familiar. Respond JSON: {email_subject: string, email_body: string, loyalty_perk: string, recommendations: string}",
        "Customer: {visitor_name} | Purchases: {previous_purchases} | LTV: ${lifetime_value} | Viewed: {products_viewed} | Top: {top_product} (${top_price})",
        [
          { field: "email_subject", as: "customer_subject", type: "string" },
          { field: "email_body", as: "customer_body", type: "string" },
          { field: "loyalty_perk", as: "loyalty_perk", type: "string" },
        ],
      ),

      // 5. AI trust-building messaging (prospect path)
      aiNode(
        "5",
        "AI generate trust-building recovery",
        760,
        "Write a browse recovery email for a new prospect (first-time visitor). Build trust: include social proof (ratings, reviews), brand story highlights, satisfaction guarantee, and a first-purchase incentive (welcome discount). Address common objections for the product category. Respond JSON: {email_subject: string, email_body: string, trust_signal: string, welcome_offer: string, recommendations: string}",
        "Prospect: {visitor_name} | Viewed: {products_viewed} | Top: {top_product} (${top_price})",
        [
          { field: "email_subject", as: "prospect_subject", type: "string" },
          { field: "email_body", as: "prospect_body", type: "string" },
          { field: "trust_signal", as: "trust_signal", type: "string" },
          { field: "welcome_offer", as: "welcome_offer", type: "string" },
        ],
        460,
      ),

      // 6. Personalized recommendations merge
      emailNode(
        "6",
        "Send customer recovery email",
        1000,
        "{visitor_email}",
        "{customer_subject}",
        "{customer_body}",
      ),

      // 7. Segmented email send (prospect path)
      emailNode(
        "7",
        "Send prospect recovery email",
        1000,
        "{visitor_email}",
        "{prospect_subject}",
        "{prospect_body}",
        460,
      ),

      // 8. Lifecycle state update + logging (Sheets)
      sheetsNode(
        "8",
        "Log lifecycle recovery",
        1240,
        "LifecycleRecovery",
        '[["{visitor_email}","{visitor_name}","{is_customer}","{previous_purchases}","{lifetime_value}","{top_product}","{top_price}","{products_viewed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // existing customer -> loyalty messaging
      e("3", "5", "output_2"), // prospect -> trust-building messaging
      e("4", "6"),
      e("5", "7"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 345. BROWSE RECOVERY SPLIT BY GEO OR STORE AVAILABILITY (8 nodes)
  // ===================================================================

  "Browse Recovery Split by Geo or Store Availability": {
    summary:
      "Geo-personalizes browse recovery based on local store availability. Detects abandonment with geolocation, checks local inventory via Shopify, routes by fulfillment method (BOPIS/local delivery/standard), generates localized copy, sends geo-personalized emails, tracks fulfillment choices, and logs regional performance.",
    nodes: [
      trigger("Webhook: Browse abandonment with geolocation", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "browse.abandoned.geolocated",
        howItStarts:
          "A visitor abandons browsing and their geolocation data is available to check local store availability",
        setup:
          "Configure your CDP or analytics to POST browse abandonment events enriched with geolocation data (city, state, zip, nearest store) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Geolocation + product extraction
      aiNode(
        "2",
        "AI extract browse + geo data",
        280,
        "Read this geo-enriched browse abandonment event and extract: visitor email, name, city, state, zip code, nearest store location, browsed product name and ID, product price, and the distance to nearest store in miles. Respond JSON: {visitor_email: string, visitor_name: string, city: string, state: string, zip: string, nearest_store: string, store_distance_miles: number, product_name: string, product_id: string, product_price: number}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "city", as: "city", type: "string" },
          { field: "state", as: "state", type: "string" },
          { field: "nearest_store", as: "nearest_store", type: "string" },
          {
            field: "store_distance_miles",
            as: "store_distance",
            type: "number",
          },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
        ],
      ),

      // 3. Local inventory check (Shopify)
      shopifyNode(
        "3",
        "Check local store inventory",
        520,
        "GET",
        "inventory_levels.json?inventory_item_ids={product_id}&location_ids={store_location_id}",
        [],
        [
          {
            name: "inventory_levels[0].available",
            customName: "local_stock",
            type: "number",
          },
          {
            name: "inventory_levels[0].location_id",
            customName: "stock_location_id",
            type: "string",
          },
        ],
      ),

      // 4. Fulfillment routing condition (BOPIS/local/standard)
      n("4", "condition", "Available for local pickup (BOPIS)?", 760, {
        conditionData: [
          {
            variable: "local_stock",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI localized copy
      aiNode(
        "5",
        "AI generate geo-personalized recovery",
        1000,
        "Write a browse recovery email personalized to the customer's location. If the product is available at their local store, lead with BOPIS (buy online, pick up in store) convenience. If not, offer local delivery if within range, or standard shipping with a speed estimate. Reference their city and nearest store. Respond JSON: {email_subject: string, email_body: string, fulfillment_method: string, delivery_estimate: string, local_advantage: string}",
        "Customer: {visitor_name} | City: {city}, {state} | Store: {nearest_store} ({store_distance} mi) | Product: {product_name} (${product_price}) | Local stock: {local_stock}",
        [
          { field: "email_subject", as: "geo_subject", type: "string" },
          { field: "email_body", as: "geo_body", type: "string" },
          {
            field: "fulfillment_method",
            as: "fulfillment_method",
            type: "string",
          },
          {
            field: "delivery_estimate",
            as: "delivery_estimate",
            type: "string",
          },
        ],
      ),

      // 6. Geo-personalized email
      emailNode(
        "6",
        "Send geo-personalized recovery email",
        1240,
        "{visitor_email}",
        "{geo_subject}",
        "{geo_body}",
      ),

      // 7. Fulfillment tracking
      sheetsNode(
        "7",
        "Log geo-recovery + fulfillment",
        1480,
        "GeoRecovery",
        '[["{visitor_email}","{visitor_name}","{city}","{state}","{nearest_store}","{store_distance}","{product_name}","{product_price}","{local_stock}","{fulfillment_method}","{delivery_estimate}"]]',
      ),

      // 8. Regional performance logging (Sheets)
      slackNode(
        "8",
        "Slack: Geo recovery sent",
        1720,
        "Geo-personalized recovery sent to {visitor_name} in {city}, {state}\\nProduct: {product_name} (${product_price})\\nStore: {nearest_store} ({store_distance} mi)\\nLocal stock: {local_stock} | Method: {fulfillment_method}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // in-store available -> BOPIS messaging
      e("4", "5", "output_2"), // not available locally -> shipping messaging
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 346. BROWSE RECOVERY SPLIT BY INVENTORY STATUS (7 nodes)
  // ===================================================================

  "Browse Recovery Split by Inventory Status": {
    summary:
      "Adapts browse recovery messaging based on real-time inventory status. Checks live inventory for abandoned products, routes by stock level (in stock/low/out), generates status-specific urgency messaging, sends recovery emails, tracks conversion lift from urgency signals, and logs inventory-aware lifecycle data.",
    nodes: [
      trigger("Webhook: Browse abandonment event", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "browse.abandoned",
        howItStarts:
          "A visitor browses products and leaves without purchasing, triggering an inventory-aware recovery sequence",
        setup:
          "Connect your analytics platform to POST browse abandonment events to the workflow URL. The workflow checks live Shopify inventory to personalize urgency.",
        inputFields: [],
      }),

      // 2. Live inventory check (Shopify)
      aiNode(
        "2",
        "AI extract browsed product data",
        280,
        "Read this browse abandonment event and extract: visitor email, name, browsed product name, product ID, variant ID, product price, product URL, and browse duration. Respond JSON: {visitor_email: string, visitor_name: string, product_name: string, product_id: string, variant_id: string, product_price: number, product_url: string, browse_minutes: number}",
        "{webhook_payload}",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "variant_id", as: "variant_id", type: "string" },
        ],
      ),

      // 3. Stock status check
      shopifyNode(
        "3",
        "Check live inventory level",
        520,
        "GET",
        "inventory_levels.json?inventory_item_ids={variant_id}",
        [],
        [
          {
            name: "inventory_levels[0].available",
            customName: "stock_level",
            type: "number",
          },
        ],
      ),

      // 4. Stock status condition (in/low/out)
      n("4", "condition", "Stock level above 10 (in stock)?", 760, {
        conditionData: [
          {
            variable: "stock_level",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI status-specific messaging
      aiNode(
        "5",
        "AI generate inventory-aware recovery",
        1000,
        "Write a browse recovery email calibrated to stock status. In stock (>10): emphasize product benefits and quality, no urgency. Low stock (1-10): add scarcity urgency ('only X left'). Out of stock (0): pivot to waitlist signup + similar alternatives. Never fake scarcity. Respond JSON: {email_subject: string, email_body: string, urgency_level: string, stock_message: string, cta_text: string}",
        "Customer: {visitor_name} | Product: {product_name} (${product_price}) | Stock: {stock_level} | URL: {product_url}",
        [
          { field: "email_subject", as: "inv_subject", type: "string" },
          { field: "email_body", as: "inv_body", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "stock_message", as: "stock_message", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 6. Recovery email
      emailNode(
        "6",
        "Send inventory-aware recovery email",
        1240,
        "{visitor_email}",
        "{inv_subject}",
        "{inv_body}",
      ),

      // 7. Inventory-aware lifecycle logging (Sheets)
      sheetsNode(
        "7",
        "Log inventory-aware recovery",
        1480,
        "InventoryRecovery",
        '[["{visitor_email}","{visitor_name}","{product_name}","{product_price}","{stock_level}","{urgency_level}","{stock_message}","{cta_text}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // in stock -> benefit-focused
      e("4", "5", "output_2"), // low/out -> urgency/alternative
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 347. BROWSE RECOVERY SPLIT BY SMS CONSENT (7 nodes)
  // ===================================================================

  "Browse Recovery Split by SMS Consent": {
    summary:
      "Routes browse recovery through the optimal channel based on SMS consent status. Checks consent and channel history, routes SMS-eligible users through Twilio first, provides email fallback, applies cross-channel suppression to prevent over-messaging, and logs channel path performance.",
    nodes: [
      trigger(
        "Webhook: Browse abandonment for channel routing",
        "webhook_app",
        {
          source: "Website Analytics / CDP",
          event: "browse.abandoned",
          howItStarts:
            "A visitor abandons browsing, triggering channel-optimized recovery based on their SMS consent status and communication preferences",
          setup:
            "Connect your analytics platform to POST browse abandonment events. Ensure your CDP includes SMS consent status and channel engagement history in the payload.",
          inputFields: [],
        },
      ),

      // 2. Consent + channel history check (runCode)
      n("2", "runCode", "Check consent + channel history", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine optimal recovery channel based on consent and history
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const smsConsent = data.sms_opted_in === true || data.sms_opted_in === "true";
const emailConsent = data.email_opted_in !== false; // default true
const lastSmsDate = data.last_sms_sent ? new Date(data.last_sms_sent) : null;
const lastEmailDate = data.last_email_sent ? new Date(data.last_email_sent) : null;
const now = Date.now();
const smsCooldown = lastSmsDate ? (now - lastSmsDate.getTime()) / (1000 * 60 * 60) : 999;
const emailCooldown = lastEmailDate ? (now - lastEmailDate.getTime()) / (1000 * 60 * 60) : 999;
const result = {
  sms_eligible: smsConsent && smsCooldown > 24 ? "true" : "false",
  email_eligible: emailConsent && emailCooldown > 12 ? "true" : "false",
  sms_consent: smsConsent ? "true" : "false",
  preferred_channel: smsConsent && smsCooldown > 24 ? "sms" : "email",
  sms_cooldown_hours: Math.round(smsCooldown),
  email_cooldown_hours: Math.round(emailCooldown),
  visitor_email: data.email || "",
  visitor_phone: data.phone || "",
  visitor_name: data.name || "there",
  product_name: data.product_name || "",
  product_price: data.product_price || 0,
  product_url: data.product_url || ""
};
return result;`,
          outputVariables: [
            { name: "sms_eligible", type: "string" },
            { name: "email_eligible", type: "string" },
            { name: "sms_consent", type: "string" },
            { name: "preferred_channel", type: "string" },
            { name: "sms_cooldown_hours", type: "number" },
            { name: "visitor_email", type: "string" },
            { name: "visitor_phone", type: "string" },
            { name: "visitor_name", type: "string" },
            { name: "product_name", type: "string" },
            { name: "product_price", type: "number" },
            { name: "product_url", type: "string" },
          ],
        },
      }),

      // 3. SMS eligible condition
      n("3", "condition", "SMS eligible (consent + cooldown)?", 520, {
        conditionData: [
          {
            variable: "sms_eligible",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. SMS-first recovery (Twilio)
      n("4", "sendSMS", "SMS: Browse recovery", 760, {
        sendSmsDetails: {
          to: "{visitor_phone}",
          message:
            "Hi {visitor_name}! Still thinking about {product_name}? It's waiting for you: {product_url}",
        },
      }),

      // 5. Email fallback path
      emailNode(
        "5",
        "Email: Browse recovery fallback",
        760,
        "{visitor_email}",
        "Still interested in {product_name}?",
        "Hi {visitor_name},\\n\\nWe noticed you were checking out {product_name} (${product_price}).\\n\\nDon't miss out — check it out again: {product_url}\\n\\nHappy shopping!",
        460,
      ),

      // 6. Cross-channel suppression (runCode)
      n("6", "runCode", "Apply cross-channel suppression", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Record the channel used and set suppression flags
const channelUsed = vars.sms_eligible === "true" ? "sms" : "email";
const now = new Date().toISOString();
const result = {
  channel_used: channelUsed,
  sent_at: now,
  suppress_other_channel_until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  recovery_id: "REC-" + Date.now()
};
return result;`,
          outputVariables: [
            { name: "channel_used", type: "string" },
            { name: "sent_at", type: "string" },
            { name: "suppress_other_channel_until", type: "string" },
            { name: "recovery_id", type: "string" },
          ],
        },
      }),

      // 7. Channel path performance logging (Sheets)
      sheetsNode(
        "7",
        "Log channel path performance",
        1240,
        "ChannelPathPerformance",
        '[["{recovery_id}","{visitor_email}","{visitor_phone}","{visitor_name}","{product_name}","{product_price}","{preferred_channel}","{channel_used}","{sms_consent}","{sms_cooldown_hours}","{sent_at}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // SMS eligible -> SMS first
      e("3", "5", "output_2"), // not SMS eligible -> email fallback
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
